// src/api/api.js
import axios from 'axios';
import { readContactsCacheList, writeContactsCacheList } from '../utils/cache.js';
import { normalizeWhatsappJid } from '../utils/whatsapp.js';
import { normalizeWhatsappConnectionsResponse } from '../utils/whatsappConnections.js';
import { normalizeContactIdentity } from '../utils/contactIdentity.js';
import { normalizePhoneForApi } from '../utils/phone.js';
import { isConversationAwaitingEvaluation } from '../utils/conversationUtils.js';
import {
  LOGOUT_EVENT,
  performLogout,
  getAuthSession,
  setAuthSession,
  setAuthToken,
} from '../utils/auth.js';
import {
  ensureSessionHealthy,
  handleUnauthorizedResponse,
  resetSessionHealth,
} from '../utils/sessionManager.js';
import { API_BASE_PATH, API_VERSION } from './constants.js';
import { WHATSAPP_SESSION_ID } from '../config.js';
import { showToast, showWarning } from '../utils/toast.js';

// --- CONFIGURAÇÃO GLOBAL DO AXIOS ---
const api = axios.create({
  baseURL: '/api', // URL base do backend FastAPI (Nginx faz proxy de /api → FastAPI)
  withCredentials: true, // garante envio de cookies httpOnly para auth/CSRF
});

let isHandlingAuthError = false;
let authFailureToastDisplayed = false;

const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro inesperado.';
const INTERNAL_CHAT_UNAVAILABLE_MESSAGE =
  'Não foi possível carregar o chat interno no momento. Tente novamente mais tarde.';
const INTERNAL_CHAT_SUPER_ADMIN_FRIENDLY_MESSAGE =
  'Este perfil não pode receber mensagens no chat interno.';
const INTERNAL_CHAT_SUPER_ADMIN_ERROR_MESSAGES = new Set([
  'não é permitido criar conversa interna com super admin.',
  'não é permitido conversar com super admin no chat interno.',
]);
const DEFAULT_CONVERSATIONS_PAGE_LIMIT = 100;
const DEFAULT_CONVERSATION_DETAIL_MESSAGES_LIMIT = 100;
const DEFAULT_CONTACTS_PAGE_LIMIT = 200;
const REQUEST_CACHE_TTLS_MS = {
  session: 2000,
  attendantMe: 10000,
  attendants: 15000,
  contacts: 15000,
  teams: 30000,
  company: 30000,
  companies: 30000,
  role: 5 * 60 * 1000,
};
const API_FETCH_ALL_PAGES_ENABLED = String(import.meta?.env?.VITE_API_FETCH_ALL_PAGES ?? '0')
  .trim()
  .toLowerCase() !== '0';
const API_FETCH_ALL_PAGES_MAX_PAGES = (() => {
  const raw = Number(import.meta?.env?.VITE_API_FETCH_ALL_PAGES_MAX_PAGES ?? 10);
  if (!Number.isFinite(raw) || raw < 1) return 10;
  return Math.trunc(raw);
})();
const IS_DEV_BUILD = Boolean(import.meta?.env?.DEV);

const normalizeOptionalCompanyId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const debugConversationsApi = (scope, payload = {}) => {
  if (!IS_DEV_BUILD) return;
  console.debug(`[conversations-api] ${scope}`, payload);
};

const resolvePaginationLimit = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) return Math.trunc(parsed);
  return fallback;
};

const resolvePaginationOffset = (value, fallback = 0) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return Math.trunc(parsed);
  return fallback;
};

const resolveFetchAllOption = (value) => {
  if (value === undefined || value === null) return API_FETCH_ALL_PAGES_ENABLED;
  return Boolean(value);
};

const requestCache = new Map();
const inFlightRequestCache = new Map();

export const clearRequestCache = () => {
  requestCache.clear();
  inFlightRequestCache.clear();
};

if (typeof window !== 'undefined' && !window.__BLUEZAP_API_CACHE_EVENTS_BOUND__) {
  window.__BLUEZAP_API_CACHE_EVENTS_BOUND__ = true;
  window.addEventListener(LOGOUT_EVENT, clearRequestCache);
  window.addEventListener('bluezap:auth-changed', clearRequestCache);
}

const getCachedRequestValue = (key, ttlMs, factory, { force = false } = {}) => {
  const now = Date.now();
  const cached = requestCache.get(key);

  if (!force) {
    if (cached?.promise) {
      return cached.promise;
    }
    if (cached && cached.expiresAt > now) {
      return Promise.resolve(cached.value);
    }
  }

  const promise = Promise.resolve()
    .then(factory)
    .then((value) => {
      requestCache.set(key, {
        value,
        expiresAt: Date.now() + ttlMs,
        promise: null,
      });
      return value;
    })
    .catch((error) => {
      const current = requestCache.get(key);
      if (current?.promise === promise) {
        requestCache.delete(key);
      }
      throw error;
    });

  requestCache.set(key, {
    value: cached?.value,
    expiresAt: now + ttlMs,
    promise,
  });

  return promise;
};

const getInFlightRequestValue = (key, factory) => {
  const cachedPromise = inFlightRequestCache.get(key);
  if (cachedPromise) {
    return cachedPromise;
  }

  const promise = Promise.resolve()
    .then(factory)
    .finally(() => {
      if (inFlightRequestCache.get(key) === promise) {
        inFlightRequestCache.delete(key);
      }
    });

  inFlightRequestCache.set(key, promise);
  return promise;
};

async function fetchPaginatedCollection(
  endpoint,
  {
    companyId = null,
    params = {},
    limit,
    offset,
    signal,
    fetchAll,
    maxPages,
    returnPage = false,
    timeout,
  } = {}
) {
  const resolvedLimit = resolvePaginationLimit(limit, DEFAULT_CONVERSATIONS_PAGE_LIMIT);
  let currentOffset = resolvePaginationOffset(offset, 0);
  const shouldFetchAll = resolveFetchAllOption(fetchAll);
  const resolvedMaxPages = maxPages === null
    ? null
    : resolvePaginationLimit(maxPages, API_FETCH_ALL_PAGES_MAX_PAGES);
  const items = [];
  let pagesFetched = 0;

  while (true) {
    const pageParams = {
      ...params,
      limit: resolvedLimit,
      offset: currentOffset,
    };
    const config = { params: pageParams };
    if (signal) config.signal = signal;
    if (timeout) config.timeout = timeout;
    const { data } = await api.get(v1CompanyId(endpoint, companyId), config);
    const hasPaginatedEnvelope = data && typeof data === 'object' && Array.isArray(data.items);
    const chunk = Array.isArray(data) ? data : (hasPaginatedEnvelope ? data.items : []);
    if (returnPage && !shouldFetchAll) {
      return hasPaginatedEnvelope
        ? data
        : {
            items: chunk,
            pagination: {
              page: Math.floor(currentOffset / resolvedLimit) + 1,
              limit: resolvedLimit,
              total: chunk.length,
              total_pages: chunk.length > 0 ? 1 : 0,
              has_next: false,
              has_previous: currentOffset > 0,
            },
          };
    }
    items.push(...chunk);
    pagesFetched += 1;

    if (!shouldFetchAll || chunk.length < resolvedLimit) {
      break;
    }
    if (resolvedMaxPages !== null && pagesFetched >= resolvedMaxPages) {
      console.warn('Limite de páginas alcançado na paginação incremental da API.', {
        endpoint,
        pagesFetched,
        maxPages: resolvedMaxPages,
        limit: resolvedLimit,
      });
      break;
    }
    currentOffset += resolvedLimit;
  }

  return items;
}

const normalizePathname = (url = '') => {
  if (!url) return '';

  const raw = url.toString();
  const [pathPart] = raw.split('?');

  if (pathPart.startsWith('http')) {
    try {
      const { pathname } = new URL(raw);
      return pathname || '';
    } catch {
      // segue para o fallback simples
    }
  }

  return pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
};

const isLoginRequest = (url = '') => {
  const path = normalizePathname(url).replace(/\/+$/, '');
  return path === `/${API_VERSION}/auth`;
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  try {
    const entries = document.cookie ? document.cookie.split(';') : [];
    for (const entry of entries) {
      const [key, ...rest] = entry.trim().split('=');
      if (key === name) {
        return decodeURIComponent(rest.join('='));
      }
    }
  } catch {
    return null;
  }
  return null;
};

const normalizeWhatsappSessionId = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const resolveWhatsappSessionId = (override) => {
  if (override !== undefined) {
    return normalizeWhatsappSessionId(override);
  }
  return normalizeWhatsappSessionId(WHATSAPP_SESSION_ID);
};

function extractDetailMessages(detail) {
  if (!detail) return [];
  if (typeof detail === 'string') return [detail];
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (typeof item.msg === 'string') return item.msg;
        if (typeof item.detail === 'string') return item.detail;
        if (typeof item.message === 'string') return item.message;
        return null;
      })
      .filter(Boolean);
  }
  return [];
}

const isLikelyHtmlPayload = (value) => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.startsWith('<!doctype html') ||
    normalized.startsWith('<html') ||
    normalized.includes('<head>') ||
    normalized.includes('<body>') ||
    normalized.includes('<title>')
  );
};

const isServerSideHttpError = (status) =>
  Number.isFinite(Number(status)) && Number(status) >= 500;

const formatErrorPayloadPreview = (payload) => {
  if (typeof payload === 'string') {
    const compact = payload.replace(/\s+/g, ' ').trim();
    return compact.slice(0, 300);
  }
  return payload ?? null;
};

export function reportInternalChatClientError(payload = {}) {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;

  let endpointPath = null;
  try {
    endpointPath = v1CompanyId('/internal-chat/client-errors');
  } catch {
    return;
  }

  const csrf = readCookie('csrf_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (csrf) {
    headers['X-CSRF-Token'] = csrf;
  }

  const report = {
    source: payload.source || 'front.internal-chat.api',
    scope: payload.scope || null,
    message: payload.message || null,
    level: payload.level || 'error',
    status: payload.status ?? null,
    method: payload.method || null,
    endpoint: payload.endpoint || null,
    content_type: payload.content_type || null,
    payload_preview:
      typeof payload.payload_preview === 'string'
        ? payload.payload_preview.slice(0, 1000)
        : payload.payload_preview ?? null,
    meta: payload.meta && typeof payload.meta === 'object' ? payload.meta : {},
  };

  void fetch(`/api${endpointPath}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify(report),
  }).catch(() => {
    // Evita ruído adicional no console em falha de telemetria.
  });
}

const logInternalChatApiError = (scope, error, message) => {
  const status = error?.response?.status ?? null;
  const method = error?.config?.method ? String(error.config.method).toUpperCase() : null;
  const endpoint = error?.config?.url || null;
  const contentType = error?.response?.headers?.['content-type'] || null;
  const rawPayload = error?.response?.data;
  const payloadPreview = formatErrorPayloadPreview(rawPayload);

  const isServerError = isServerSideHttpError(status);
  const logger = isServerError ? console.error : console.warn;
  logger(`[Internal chat API] ${scope}`, {
    message,
    status,
    method,
    endpoint,
    contentType,
    payloadPreview,
  });

  reportInternalChatClientError({
    source: 'front.internal-chat.api',
    scope,
    message,
    level: isServerError ? 'error' : 'warning',
    status,
    method,
    endpoint,
    content_type: contentType,
    payload_preview: payloadPreview,
  });
};

export function getApiErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  if (!error) return fallback;
  const data = error?.response?.data;
  const status = error?.response?.status;
  const responseContentType = String(error?.response?.headers?.['content-type'] || '').toLowerCase();
  let candidateMessage = null;

  if (data) {
    if (typeof data === 'string') {
      candidateMessage = data;
    } else if (typeof data.detail === 'string') {
      candidateMessage = data.detail;
    } else {
      const detailMessages = extractDetailMessages(data.detail);
      if (detailMessages.length > 0) {
        candidateMessage = detailMessages.join(' ');
      } else if (typeof data.message === 'string') {
        candidateMessage = data.message;
      } else if (typeof data.error === 'string') {
        candidateMessage = data.error;
      }
    }
  }

  if (typeof candidateMessage === 'string') {
    const trimmed = candidateMessage.trim();
    const hasHtmlContent = isLikelyHtmlPayload(trimmed) || responseContentType.includes('text/html');
    if (hasHtmlContent) {
      return fallback;
    }
    return trimmed || fallback;
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    const message = error.message.trim();
    if (isLikelyHtmlPayload(message)) {
      return fallback;
    }
    return message;
  }

  if (isServerSideHttpError(status)) {
    return fallback;
  }

  return fallback;
}

function getInternalChatApiErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  if (isServerSideHttpError(error?.response?.status)) {
    return INTERNAL_CHAT_UNAVAILABLE_MESSAGE;
  }

  const message = getApiErrorMessage(error, fallback);
  const normalizedMessage = String(message || '').trim().toLowerCase();

  if (INTERNAL_CHAT_SUPER_ADMIN_ERROR_MESSAGES.has(normalizedMessage)) {
    return INTERNAL_CHAT_SUPER_ADMIN_FRIENDLY_MESSAGE;
  }

  return message;
}

export const AUTH_FAILURE_REASONS = {
  LOCAL_EXPIRATION: 'expiração local',
  HEALTH_CHECK_401: 'health-check 401',
  REFRESH_FAILED: 'refresh falhou',
  NETWORK_ERROR: 'erro de rede',
};

const isNetworkFailurePattern = (value) => {
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return (
    normalized.includes('network error') ||
    normalized.includes('networkerror') ||
    normalized.includes('failed to fetch')
  );
};

export const isNetworkRelatedError = (candidate) => {
  if (!candidate) return false;
  if (typeof candidate === 'string') {
    return isNetworkFailurePattern(candidate);
  }
  if (typeof candidate === 'object') {
    if (candidate && typeof candidate.code === 'string' && candidate.code === 'ERR_NETWORK') {
      return true;
    }
    if (typeof candidate.message === 'string' && isNetworkFailurePattern(candidate.message)) {
      return true;
    }
    if (candidate && candidate.name === 'TypeError' && isNetworkFailurePattern(candidate.message)) {
      return true;
    }
  }
  return false;
};

const resolveAuthFailureStatus = ({ status, reason, error } = {}) => {
  if (Number.isFinite(status)) return status;
  if (error?.response?.status) return error.response.status;
  if (reason === AUTH_FAILURE_REASONS.HEALTH_CHECK_401) return 401;
  return undefined;
};

const getAuthFailureToastMessage = ({ status, code, reason }) => {
  if (status === 401 || status === 403) {
    return 'Sessão expirada. Faça login novamente.';
  }
  if (status === 500) {
    return 'Instabilidade no servidor. Tente novamente em alguns instantes.';
  }
  if (reason === AUTH_FAILURE_REASONS.NETWORK_ERROR || code === 'ERR_NETWORK') {
    return 'Problema de rede. Verifique sua conexão e tente novamente.';
  }
  return 'Não foi possível validar a sessão. Verifique sua conexão e tente novamente.';
};

const logAuthFailure = ({ endpoint = 'desconhecido', status, reason, error }) => {
  console.warn('handleAuthFailure', {
    endpoint,
    status: status ?? 'N/A',
    motivo: reason,
    error: error || null,
  });
};

const handleAuthFailure = (metadata = {}) => {
  if (isHandlingAuthError) return;
  isHandlingAuthError = true;

  const reason = metadata.reason || AUTH_FAILURE_REASONS.LOCAL_EXPIRATION;
  const endpoint =
    metadata.endpoint || metadata.error?.config?.url || metadata.error?.request?.url || 'desconhecido';
  const status = resolveAuthFailureStatus({
    status: metadata.status,
    reason,
    error: metadata.error,
  });

  logAuthFailure({ endpoint, status, reason, error: metadata.error });

  const shouldLogout = status === 401 || status === 403;
  if (shouldLogout) {
    resetSessionHealth();
    performLogout();
  }

  if (!authFailureToastDisplayed) {
    authFailureToastDisplayed = true;
    const toastMessage = getAuthFailureToastMessage({
      status,
      code: metadata.error?.code,
      reason,
    });
    showWarning?.(toastMessage);
  }

  setTimeout(() => {
    isHandlingAuthError = false;
  }, 100);
};

const isAuthFailureReason = (reason) => {
  if (!reason) return false;
  if (typeof reason === 'string') {
    return reason.includes('Auth 401') || reason.includes('Auth 403');
  }
  const status = reason?.status ?? reason?.response?.status;
  return status === 401 || status === 403;
};

const isAuthRecoveryEligible = (error) => {
  const status = error?.response?.status;
  if (status === 401) {
    return true;
  }
  if (status !== 403) {
    return false;
  }

  const requestUrl = String(error?.config?.url || '');
  if (requestUrl.includes('/auth/session')) {
    return true;
  }

  const detail = getApiErrorMessage(error, '').trim().toLowerCase();
  if (!detail) {
    return false;
  }

  if (detail.includes('not enough permissions')) {
    return false;
  }

  return (
    detail.includes('company is inactive') ||
    detail.includes('could not validate credentials') ||
    detail.includes('missing authentication token') ||
    detail.includes('user is inactive')
  );
};

const resolveAuthStatusFromReason = (reason) => {
  if (!reason) return undefined;
  const status = reason?.status ?? reason?.response?.status;
  if (status === 401 || status === 403) return status;
  if (typeof reason === 'string') {
    const match = reason.match(/Auth\s+(\d+)/i);
    if (match) {
      const parsed = Number(match[1]);
      if (parsed === 401 || parsed === 403) return parsed;
    }
  }
  return undefined;
};

const getHealthFailureReason = (health) => {
  if (!health) return AUTH_FAILURE_REASONS.LOCAL_EXPIRATION;
  if (isNetworkRelatedError(health.reason)) {
    return AUTH_FAILURE_REASONS.NETWORK_ERROR;
  }
  if (isAuthFailureReason(health.reason)) {
    return AUTH_FAILURE_REASONS.HEALTH_CHECK_401;
  }
  return AUTH_FAILURE_REASONS.LOCAL_EXPIRATION;
};

api.interceptors.request.use(
  async (config) => {
    const rawUrl = typeof config.url === 'string' ? config.url : '';
    const normalizedPath = normalizePathname(rawUrl);
    if (!normalizedPath) return config;

    const [, queryString] = rawUrl.split('?');
    const isLogin = isLoginRequest(normalizedPath);
    const isAuthEndpoint = normalizedPath.startsWith(`/${API_VERSION}/auth`);

    if (!isAuthEndpoint) {
      const health = await ensureSessionHealthy();
      if (!health.allowed) {
        const healthReasonMessage =
          typeof health.reason === 'string'
            ? health.reason
            : health.reason?.message ?? 'não foi possível validar a sessão';
        const failureReason = getHealthFailureReason(health);
        const failureStatus =
          failureReason === AUTH_FAILURE_REASONS.HEALTH_CHECK_401
            ? resolveAuthStatusFromReason(health.reason)
            : undefined;
        handleAuthFailure({
          endpoint: normalizedPath,
          reason: failureReason,
          status: failureStatus,
          error: health.reason,
        });
        const healthError = new Error(
          `Sessão inválida ou expirada (${healthReasonMessage}).`
        );
        healthError.isSessionHealthError = true;
        healthError.sessionHealthStatus = health.status;
        healthError.sessionHealthReason = health.reason;
        return Promise.reject(healthError);
      }
    }

    const pathWithoutLeadingSlash = normalizedPath.startsWith('/')
      ? normalizedPath.slice(1)
      : normalizedPath;
    const session = getAuthSession();

    // Prefixo /v1[/company_id] nas rotas que não incluíram explicitamente
    if (!normalizedPath.startsWith(`/${API_VERSION}`)) {
      const companyId = session.companyId;
      const basePrefix = companyId ? `/${API_VERSION}/${companyId}` : `/${API_VERSION}`;
      config.url = `${basePrefix}/${pathWithoutLeadingSlash}`;
    } else {
      config.url = normalizedPath;
    }

    if (queryString) {
      config.url = `${config.url}?${queryString}`;
    }

    config.headers = config.headers || {};

    if (isLogin) {
      if (config.headers.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    // CSRF: envia token do cookie quando presente
    const csrf = readCookie('csrf_token');
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || '';
    if (error?.isTokenExpired) {
      return Promise.reject(error);
    }

    if (isAuthRecoveryEligible(error) && !requestUrl.includes('/auth')) {
      const recovery = await handleUnauthorizedResponse(status);
      if (!recovery?.recovered) {
        const failureReason =
          recovery?.reason === AUTH_FAILURE_REASONS.NETWORK_ERROR ||
          isNetworkRelatedError(error)
            ? AUTH_FAILURE_REASONS.NETWORK_ERROR
            : AUTH_FAILURE_REASONS.REFRESH_FAILED;
        handleAuthFailure({
          endpoint: requestUrl,
          status,
          reason: failureReason,
          error,
        });
      }
    }
    return Promise.reject(error);
  }
);

const AUTH_LOGOUT_URL = `${API_BASE_PATH}/auth/logout`;

// --- AUTENTICAÇÃO ---
export async function login(email, password) {
  try {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await api.post(`/${API_VERSION}/auth`, params.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    const {
      company_id,
      attendant_id,
      access_token: accessToken,
      token_expires_at: tokenExpiresAt,
    } = response.data;
    setAuthSession({
      companyId: company_id ?? null,
      attendantId: attendant_id ?? null,
    });
    clearRequestCache();
    setAuthToken(accessToken, {
      expiresAt: tokenExpiresAt,
      accessible: false,
    });
    resetSessionHealth();
    authFailureToastDisplayed = false;

    // Solicita permissão de notificação (silencioso quando não suportado/decidido)
    try {
      if (typeof Notification === 'undefined') {
        showToast('Seu navegador não suporta notificações.');
      } else if (Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        if (perm === 'granted') {
          showToast('Notificações ativadas. Você verá alertas quando a aba não estiver em foco.');
        } else if (perm === 'denied') {
          showToast('Notificações bloqueadas no navegador. Você pode ativar nas configurações do site.');
        }
      }
    } catch (e) {
      console.warn('Permissão de notificação não pôde ser solicitada:', e);
    }
    return { success: true, attendant_id, company_id };
  } catch (error) {
    console.error('Erro ao fazer login:', error.response?.data || error.message);
    return { success: false, error: error.response?.data || error.message };
  }
}

export async function requestPasswordReset(email) {
  const response = await api.post(`/${API_VERSION}/auth/forgot-password`, { email });
  return response.data;
}

export async function resetPassword(token, newPassword) {
  const response = await api.post(`/${API_VERSION}/auth/reset-password`, {
    token,
    new_password: newPassword,
  });
  return response.data;
}

export async function logoutAttendant(options = {}) {
  const { useBeacon = false } = options ?? {};

  if (useBeacon) {
    const hasBeacon =
      typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';

    if (hasBeacon) {
      try {
        const payload = '{}';
        const blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(AUTH_LOGOUT_URL, blob)) {
          clearRequestCache();
          return { success: true };
        }
      } catch (error) {
        console.warn('Erro ao enviar logout com sendBeacon:', error);
      }
    }
  }

  try {
    await api.post(`/${API_VERSION}/auth/logout`, {});
    clearRequestCache();
    resetSessionHealth();
    return { success: true };
  } catch (error) {
    const status = error?.response?.status;
    // Se o token expirou ou já foi removido no backend, tratamos como sucesso silencioso
    if (status === 401 || status === 403) {
      clearRequestCache();
      resetSessionHealth();
      return { success: true, alreadyLoggedOut: true };
    }
    console.error('Erro ao fazer logout:', error.response?.data || error.message);
    return { success: false, error: error.response?.data || error.message };
  }
}

// Sessão atual (usa cookie httpOnly)
export async function getSession() {
  return getCachedRequestValue(
    'session:current',
    REQUEST_CACHE_TTLS_MS.session,
    async () => {
      const { data } = await api.get(`/${API_VERSION}/auth/session`);
      return data;
    }
  );
}

// --- ATENDENTES ---
export async function getAttendants() {
  try {
    const companyScope = getCompanyId() ?? 'current';
    return await getCachedRequestValue(
      `attendants:list:${companyScope}`,
      REQUEST_CACHE_TTLS_MS.attendants,
      async () => {
        const response = await api.get('/attendants');
        return response.data;
      }
    );
  } catch (error) {
    console.error('Erro ao buscar atendentes:', error.response?.data || error.message);
    throw error;
  }
}

export async function getAttendantMe(companyIdOverride = null, options = {}) {
  try {
    const companyScope = companyIdOverride ?? getCompanyId() ?? 'current';
    return await getCachedRequestValue(
      `attendant:me:${companyScope}`,
      REQUEST_CACHE_TTLS_MS.attendantMe,
      async () => {
        const { data } = await api.get(v1CompanyId('/attendants/me', companyIdOverride));
        return data;
      },
      { force: options?.force === true }
    );
  } catch (error) {
    console.error('Erro ao buscar atendentes:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateAttendantMe(data) {
  try {
    const { data: responseData } = await api.put(v1CompanyId('/attendants/me'), data);
    return responseData;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao atualizar seu perfil.');
    console.error('Erro ao atualizar perfil do atendente:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function createAttendant(data) {
  try {
    const response = await api.post('/attendants', data);
    return response.data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao criar atendente.');
    console.error('Erro ao criar atendente:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    if (error && typeof error === 'object') {
      normalizedError.response = error.response;
      normalizedError.request = error.request;
      normalizedError.config = error.config;
    }
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function updateAttendant(id, data) {
  try {
    const response = await api.put(`/attendants/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar atendente ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteAttendant(id) {
  try {
    await api.delete(`/attendants/${id}`);
    return true;
  } catch (error) {
    console.error(`Erro ao deletar atendente ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- ANALYTICS ---
export async function getAnalyticsOverview({ startDate, endDate } = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const { data } = await api.get(v1CompanyId('/analytics/overview'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar overview de analytics.');
    console.error('Analytics overview error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getAttendantAnalytics({ startDate, endDate } = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const { data } = await api.get(v1CompanyId('/analytics/attendants'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar analytics por atendente.');
    console.error('Attendant analytics error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getConversationAnalytics({
  startDate,
  endDate,
  attendantId,
  teamId,
} = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const parsedAttendantId =
      attendantId === '' || attendantId === null || attendantId === undefined
        ? null
        : Number(attendantId);
    if (!Number.isNaN(parsedAttendantId) && parsedAttendantId !== null && parsedAttendantId !== undefined) {
      params.attendant_id = parsedAttendantId;
    }

    const parsedTeamId =
      teamId === '' || teamId === null || teamId === undefined ? null : Number(teamId);
    if (!Number.isNaN(parsedTeamId) && parsedTeamId !== null && parsedTeamId !== undefined) {
      params.team_id = parsedTeamId;
    }

    const { data } = await api.get(v1CompanyId('/analytics/conversations'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar analytics de atendimentos.');
    console.error('Conversation analytics error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getConversationTeamBreakdownDetails({
  startDate,
  endDate,
  attendantId,
  teamId,
  teamKey,
} = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const parsedAttendantId =
      attendantId === '' || attendantId === null || attendantId === undefined
        ? null
        : Number(attendantId);
    if (!Number.isNaN(parsedAttendantId) && parsedAttendantId !== null && parsedAttendantId !== undefined) {
      params.attendant_id = parsedAttendantId;
    }

    const parsedTeamId =
      teamId === '' || teamId === null || teamId === undefined ? null : Number(teamId);
    if (!Number.isNaN(parsedTeamId) && parsedTeamId !== null && parsedTeamId !== undefined) {
      params.team_id = parsedTeamId;
    }
    if (teamKey) params.team_key = teamKey;

    const { data } = await api.get(v1CompanyId('/analytics/conversations/team-breakdown'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar atendimentos da fila.');
    console.error('Conversation team breakdown details error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getConversationOriginBreakdownDetails({
  startDate,
  endDate,
  attendantId,
  teamId,
  teamKey,
  originKey,
} = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const parsedAttendantId =
      attendantId === '' || attendantId === null || attendantId === undefined
        ? null
        : Number(attendantId);
    if (!Number.isNaN(parsedAttendantId) && parsedAttendantId !== null && parsedAttendantId !== undefined) {
      params.attendant_id = parsedAttendantId;
    }

    const parsedTeamId =
      teamId === '' || teamId === null || teamId === undefined ? null : Number(teamId);
    if (!Number.isNaN(parsedTeamId) && parsedTeamId !== null && parsedTeamId !== undefined) {
      params.team_id = parsedTeamId;
    }
    if (teamKey) params.team_key = teamKey;
    if (originKey) params.origin_key = originKey;

    const { data } = await api.get(v1CompanyId('/analytics/conversations/origin-breakdown'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar origem dos atendimentos.');
    console.error('Conversation origin breakdown details error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getMessageTrafficAnalytics({
  startDate,
  endDate,
  attendantId,
  teamId,
} = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const parsedAttendantId =
      attendantId === '' || attendantId === null || attendantId === undefined
        ? null
        : Number(attendantId);
    if (!Number.isNaN(parsedAttendantId) && parsedAttendantId !== null && parsedAttendantId !== undefined) {
      params.attendant_id = parsedAttendantId;
    }

    const parsedTeamId =
      teamId === '' || teamId === null || teamId === undefined ? null : Number(teamId);
    if (!Number.isNaN(parsedTeamId) && parsedTeamId !== null && parsedTeamId !== undefined) {
      params.team_id = parsedTeamId;
    }

    const { data } = await api.get(v1CompanyId('/analytics/messages'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar relatório de mensagens.');
    console.error('Message traffic analytics error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getSatisfactionAnalytics({
  startDate,
  endDate,
  attendantId,
  teamId,
  includeCommentsOnly,
  satisfactionMin,
  satisfactionMax,
  limit,
  offset,
} = {}) {
  try {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const parsedAttendantId =
      attendantId === '' || attendantId === null || attendantId === undefined
        ? null
        : Number(attendantId);
    if (!Number.isNaN(parsedAttendantId) && parsedAttendantId !== null && parsedAttendantId !== undefined) {
      params.attendant_id = parsedAttendantId;
    }

    const parsedTeamId =
      teamId === '' || teamId === null || teamId === undefined ? null : Number(teamId);
    if (!Number.isNaN(parsedTeamId) && parsedTeamId !== null && parsedTeamId !== undefined) {
      params.team_id = parsedTeamId;
    }

    if (includeCommentsOnly !== undefined) {
      params.include_comments_only = includeCommentsOnly;
    }

    const parsedMin =
      satisfactionMin === '' || satisfactionMin === null || satisfactionMin === undefined
        ? null
        : Number(satisfactionMin);
    if (!Number.isNaN(parsedMin) && parsedMin !== null && parsedMin !== undefined) {
      params.satisfaction_min = parsedMin;
    }

    const parsedMax =
      satisfactionMax === '' || satisfactionMax === null || satisfactionMax === undefined
        ? null
        : Number(satisfactionMax);
    if (!Number.isNaN(parsedMax) && parsedMax !== null && parsedMax !== undefined) {
      params.satisfaction_max = parsedMax;
    }
    const parsedLimit =
      limit === '' || limit === null || limit === undefined ? null : Number(limit);
    if (!Number.isNaN(parsedLimit) && parsedLimit !== null && parsedLimit !== undefined) {
      params.limit = parsedLimit;
    }
    const parsedOffset =
      offset === '' || offset === null || offset === undefined ? null : Number(offset);
    if (!Number.isNaN(parsedOffset) && parsedOffset !== null && parsedOffset !== undefined) {
      params.offset = parsedOffset;
    }

    const { data } = await api.get(v1CompanyId('/analytics/satisfaction'), { params });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar relatório de satisfação.');
    console.error('Satisfaction analytics error:', message, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function listInternalChatAttendants({ search, limit, offset } = {}) {
  try {
    const params = {};
    const normalizedSearch = typeof search === 'string' ? search.trim() : '';
    if (normalizedSearch) params.search = normalizedSearch;
    const parsedLimit = Number(limit);
    if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
      params.limit = Math.trunc(parsedLimit);
    }
    const parsedOffset = Number(offset);
    if (Number.isFinite(parsedOffset) && parsedOffset >= 0) {
      params.offset = Math.trunc(parsedOffset);
    }
    const { data } = await api.get(v1CompanyId('/internal-chat/attendants'), { params });
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, INTERNAL_CHAT_UNAVAILABLE_MESSAGE);
    logInternalChatApiError('list attendants failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function listInternalChatThreads({ participantAttendantId, limit, offset } = {}) {
  try {
    const params = {};
    const parsedParticipantId = Number(participantAttendantId);
    if (Number.isFinite(parsedParticipantId) && parsedParticipantId > 0) {
      params.participant_attendant_id = parsedParticipantId;
    }
    const parsedLimit = Number(limit);
    if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
      params.limit = Math.trunc(parsedLimit);
    }
    const parsedOffset = Number(offset);
    if (Number.isFinite(parsedOffset) && parsedOffset >= 0) {
      params.offset = Math.trunc(parsedOffset);
    }
    const { data } = await api.get(v1CompanyId('/internal-chat/threads'), { params });
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, INTERNAL_CHAT_UNAVAILABLE_MESSAGE);
    logInternalChatApiError('list threads failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function createInternalChatThread(targetAttendantId) {
  try {
    const { data } = await api.post(v1CompanyId('/internal-chat/threads'), {
      target_attendant_id: targetAttendantId,
    });
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, 'Erro ao iniciar a conversa.');
    logInternalChatApiError('create thread failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getInternalChatThreadMessages(threadId, { sinceId, beforeId, limit } = {}) {
  try {
    const params = {};
    if (sinceId) params.since_id = sinceId;
    if (beforeId) params.before_id = beforeId;
    if (limit) params.limit = limit;
    const { data } = await api.get(v1CompanyId(`/internal-chat/threads/${threadId}/messages`), {
      params,
    });
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, INTERNAL_CHAT_UNAVAILABLE_MESSAGE);
    logInternalChatApiError('list thread messages failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getInternalChatUnreadSummary() {
  try {
    const { data } = await api.get(v1CompanyId('/internal-chat/unread'));
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, INTERNAL_CHAT_UNAVAILABLE_MESSAGE);
    logInternalChatApiError('get unread summary failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function markInternalChatThreadRead(threadId, throughMessageId = null) {
  try {
    const payload = {};
    const parsedMessageId = Number(throughMessageId);
    if (Number.isFinite(parsedMessageId) && parsedMessageId > 0) {
      payload.through_message_id = Math.trunc(parsedMessageId);
    }
    const { data } = await api.post(
      v1CompanyId(`/internal-chat/threads/${threadId}/read`),
      payload
    );
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, INTERNAL_CHAT_UNAVAILABLE_MESSAGE);
    logInternalChatApiError('mark thread read failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function sendInternalChatThreadMessage(
  threadId,
  { content, file, messageType, metadata } = {}
) {
  try {
    const formData = new FormData();
    if (content) formData.append('content', content);
    if (messageType) formData.append('message_type', messageType);
    if (metadata && typeof metadata === 'object') {
      formData.append('metadata', JSON.stringify(metadata));
    }
    if (file) formData.append('file', file);
    const { data } = await api.post(
      v1CompanyId(`/internal-chat/threads/${threadId}/messages`),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  } catch (error) {
    const message = getInternalChatApiErrorMessage(error, 'Erro ao enviar a mensagem interna.');
    logInternalChatApiError('send message failed', error, message);
    const normalizedError = new Error(message);
    normalizedError.status = error?.response?.status ?? null;
    normalizedError.endpoint = error?.config?.url ?? null;
    normalizedError.cause = error;
    throw normalizedError;
  }
}

// --- Helper para obter company_id (snapshot > token) ---
function getCompanyId() {
  const session = getAuthSession();
  const normalizedSessionCompany = normalizeOptionalCompanyId(session?.companyId);
  if (normalizedSessionCompany !== null) {
    return normalizedSessionCompany;
  }

  return null; // sem fallback: exige seleção explícita
}

const normalizeRelativePath = (path = '') => {
  if (!path) return '';
  return path.startsWith('/') ? path.slice(1) : path;
};

function v1CompanyId(path = '', companyIdOverride = null) {
  const companyId = normalizeOptionalCompanyId(companyIdOverride ?? getCompanyId());
  if (companyId === null) {
    throw new Error('company_id não definido; selecione uma empresa antes de continuar.');
  }
  const relative = normalizeRelativePath(path);
  if (!relative) {
    return `/v1/${companyId}`;
  }
  return `/v1/${companyId}/${relative}`;
}

function v1(path = '') {
  const relative = normalizeRelativePath(path);
  if (!relative) {
    return `/v1`;
  }
  return `/v1/${relative}`;
}

// ---- CLIENTES CACHE ----
const CUSTOMERS_CACHE_KEY = 'clients';
const CUSTOMERS_CACHE_TIMESTAMP_KEY = 'clients_cached_at';
export const CUSTOMERS_CACHE_DEFAULT_MAX_AGE = 5 * 60 * 1000; // 5 minutos

const normalizeCustomers = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.results)) return raw.results;
  }
  return [];
};

const readCustomersCache = () => {
  try {
    const rawData = localStorage.getItem(CUSTOMERS_CACHE_KEY);
    if (!rawData) return null;

    const customers = normalizeCustomers(JSON.parse(rawData));
    const timestampRaw = localStorage.getItem(CUSTOMERS_CACHE_TIMESTAMP_KEY);
    const cachedAt = timestampRaw ? Number(timestampRaw) : 0;

    return { customers, cachedAt };
  } catch {
    return null;
  }
};

const writeCustomersCache = (customers) => {
  try {
    const normalized = normalizeCustomers(customers);
    localStorage.setItem(CUSTOMERS_CACHE_KEY, JSON.stringify(normalized));
    localStorage.setItem(CUSTOMERS_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (err) {
    console.warn('Não foi possível armazenar clientes no cache local:', err);
  }
};

const isCacheFresh = (cachedAt, maxAge) => {
  if (!cachedAt) return false;
  if (!Number.isFinite(maxAge) || maxAge <= 0) return false;
  return Date.now() - cachedAt <= maxAge;
};

export const invalidateCustomersCache = () => {
  localStorage.removeItem(CUSTOMERS_CACHE_KEY);
  localStorage.removeItem(CUSTOMERS_CACHE_TIMESTAMP_KEY);
};

export async function getCustomers(options = {}) {
  const {
    useCache = false,
    forceRefresh = false,
    cacheMaxAge = CUSTOMERS_CACHE_DEFAULT_MAX_AGE,
  } = options;
  const resolvedFetchAll = resolveFetchAllOption(options?.fetchAll ?? false);
  const resolvedMaxPages =
    options?.maxPages === null || options?.maxPages === undefined
      ? null
      : resolvePaginationLimit(options.maxPages, API_FETCH_ALL_PAGES_MAX_PAGES);
  const companyScope = getCompanyId() ?? 'current';
  const inFlightKey = JSON.stringify({
    resource: 'customers',
    companyScope,
    fetchAll: resolvedFetchAll,
    maxPages: resolvedMaxPages,
  });

  const cacheEntry = readCustomersCache();
  if (useCache && !forceRefresh && cacheEntry && isCacheFresh(cacheEntry.cachedAt, cacheMaxAge)) {
    return cacheEntry.customers;
  }

  try {
    const normalized = await getInFlightRequestValue(inFlightKey, async () => {
      const data = await fetchPaginatedCollection('/contacts', {
        limit: DEFAULT_CONTACTS_PAGE_LIMIT,
        offset: 0,
        fetchAll: resolvedFetchAll,
        maxPages: resolvedMaxPages,
      });
      const nextCustomers = normalizeCustomers(data);
      writeCustomersCache(nextCustomers);
      return nextCustomers;
    });
    return normalized;
  } catch (error) {
    if (useCache && cacheEntry?.customers?.length) {
      console.warn('Erro ao buscar clientes, utilizando cache local:', error);
      return cacheEntry.customers;
    }
    throw error;
  }
}

export async function getCustomerById(id) {
  const { data } = await api.get(v1CompanyId(`/contacts/${id}`));
  return data;
}

export async function createCustomer(formData) {
  const payload = buildContactFormData(formData);
  const { data } = await api.post(v1CompanyId("/contacts"), payload);
  invalidateCustomersCache();
  upsertContactIntoLocalCache(data);
  return data;
}

export async function updateCustomer(id, formData) {
  const payload = buildContactFormData(formData);
  const { data } = await api.put(v1CompanyId(`/contacts/${id}`), payload);
  invalidateCustomersCache();
  upsertContactIntoLocalCache(data);
  return data;
}

export async function deleteCustomer(id) {
  try {
    await api.delete(v1CompanyId(`/contacts/${id}`));
    invalidateCustomersCache();
    return true;
  } catch (error) {
    console.error(`Erro ao deletar cliente ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function importCustomers(file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(v1CompanyId('/contacts/import'), formData);
  const { data, headers } = response || {};
  const skippedExistingHeader = Number(headers?.['x-import-skipped-existing'] || 0) || 0;
  const skippedDuplicatesHeader = Number(headers?.['x-import-skipped-duplicates'] || 0) || 0;
  const createdHeader = Number(headers?.['x-import-created'] || 0) || 0;
  const payload = data || {};
  const createdList = Array.isArray(payload) ? payload : Array.isArray(payload.created) ? payload.created : [];
  const existingList = Array.isArray(payload.existing) ? payload.existing : [];
  const skippedExisting = Number(payload.skipped_existing ?? payload.skippedExisting ?? skippedExistingHeader) || 0;
  const skippedDuplicates = Number(payload.skipped_duplicates ?? payload.skippedDuplicates ?? skippedDuplicatesHeader) || 0;
  const created = Number(payload.created_count ?? payload.createdCount ?? createdHeader) || createdList.length;
  invalidateCustomersCache();
  return { created: createdList, existing: existingList, skippedExisting, skippedDuplicates, createdCount: created };
}

// ---- CONTATOS (alinhado ao backend) ----
function normalizeDigits(v) {
  return (v ?? "").toString().replace(/\D/g, "");
}

function normalizeConversationContactFilter(value) {
  const normalizedIdentity = normalizeContactIdentity(value);
  if (normalizedIdentity) {
    return normalizedIdentity;
  }
  return normalizeDigits(value);
}

function isCanceledRequestError(error) {
  if (!error) return false;
  if (typeof axios.isCancel === 'function' && axios.isCancel(error)) return true;
  if (error?.__CANCEL__ === true) return true;

  const errorCode = String(error?.code || '').toUpperCase();
  if (errorCode === 'ERR_CANCELED') return true;

  const errorName = String(error?.name || '').toLowerCase();
  if (errorName.includes('cancel') || errorName.includes('abort')) return true;

  const errorMessage = String(error?.message || '').toLowerCase();
  if (errorMessage.includes('canceled') || errorMessage.includes('cancelled')) return true;
  if (errorMessage.includes('aborted')) return true;

  return false;
}

function isFormDataObject(value) {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function base64StringToBlob(base64, mimeType = 'application/octet-stream') {
  if (typeof atob !== 'function' || typeof Blob === 'undefined') {
    return null;
  }

  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  } catch {
    return null;
  }
}

function resolveContactPhotoFile(contact) {
  if (typeof Blob === 'undefined') return null;

  const candidate = contact?._file || contact?.photograph || contact?.photo;
  if (!candidate) return null;

  if (typeof File !== 'undefined' && candidate instanceof File) {
    return { file: candidate, filename: candidate.name || 'contact-photo' };
  }

  if (candidate instanceof Blob) {
    return { file: candidate, filename: 'contact-photo' };
  }

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith('data:')) {
      const [meta, data] = trimmed.split(',');
      if (!meta || !data) return null;
      const mimeMatch = meta.match(/^data:([^;]+);base64$/i);
      const mime = mimeMatch?.[1] || 'application/octet-stream';
      const blob = base64StringToBlob(data, mime);
      if (!blob) return null;
      const extension = mime.split('/')[1] || 'bin';
      return { file: blob, filename: `contact-photo.${extension}` };
    }

    const blob = base64StringToBlob(trimmed, 'application/octet-stream');
    if (blob) {
      return { file: blob, filename: 'contact-photo.bin' };
    }
  }

  return null;
}

function buildContactFormData(contact) {
  if (isFormDataObject(contact)) {
    return contact;
  }

  const data = new FormData();
  const safeName = (contact?.name ?? contact?.contact_name ?? '').toString().trim();
  data.append('name', safeName);

  const whatsappNumberForApi = normalizePhoneForApi(
    contact?.whatsapp_number ?? contact?.whatsapp
  );
  if (whatsappNumberForApi) {
    data.append('whatsapp_number', whatsappNumberForApi);
  }

  const contactPhoneForApi = normalizePhoneForApi(
    contact?.contact_phone ?? contact?.phone_number ?? contact?.phone
  );
  if (contactPhoneForApi) {
    data.append('contact_phone', contactPhoneForApi);
  }

  const optionalFields = [
    'email',
    'contact_name',
    'address',
    'tag',
    'phone_digits',
    'display_name',
    'display_name_source',
    'identity_source',
    'whatsapp_name',
    'whatsapp_jid',
    'whatsapp_lid',
  ];

  optionalFields.forEach((field) => {
    const value = contact?.[field];
    if (value === undefined || value === null) return;
    const normalized = typeof value === 'string' ? value.trim() : value;
    if (normalized === '') return;
    data.append(field, normalized);
  });

  if (!data.has('display_name')) {
    const displayNameFallback = (contact?.display_name || contact?.name || contact?.contact_name || '').toString().trim();
    if (displayNameFallback) {
      data.append('display_name', displayNameFallback);
      data.append('display_name_source', (contact?.display_name_source || 'manual').toString().trim().toLowerCase());
    }
  }

  const photo = resolveContactPhotoFile(contact);
  if (photo) {
    data.append('photograph', photo.file, photo.filename);
  }

  return data;
}

// Lista todos os contatos da empresa atual.
export async function listAllCompanyContacts(options = {}) {
  if (options?.signal) {
    const data = await fetchPaginatedCollection('/contacts', {
      limit: resolvePaginationLimit(options?.limit, DEFAULT_CONTACTS_PAGE_LIMIT),
      offset: 0,
      fetchAll: resolveFetchAllOption(options?.fetchAll ?? false),
      maxPages: options?.maxPages ?? null,
      signal: options?.signal,
    });
    const normalized = Array.isArray(data) ? data : [];
    try {
      writeContactsCacheList(normalized);
    } catch {
      // ignore cache errors
    }
    return normalized;
  }

  const companyScope = getCompanyId() ?? 'current';
  return getCachedRequestValue(
    `contacts:all:${companyScope}`,
    REQUEST_CACHE_TTLS_MS.contacts,
    async () => {
      const data = await fetchPaginatedCollection('/contacts', {
        limit: resolvePaginationLimit(options?.limit, DEFAULT_CONTACTS_PAGE_LIMIT),
        offset: 0,
        fetchAll: resolveFetchAllOption(options?.fetchAll ?? false),
        maxPages: options?.maxPages ?? null,
      });
      const normalized = Array.isArray(data) ? data : [];
      try {
        writeContactsCacheList(normalized);
      } catch {
        // ignore cache errors
      }
      return normalized;
    },
    { force: options?.force === true }
  );
}

const parsePaginationHeaders = (headers = {}, fallbackItems = [], fallbackLimit = DEFAULT_CONTACTS_PAGE_LIMIT, fallbackOffset = 0) => {
  const totalHeader = Number(headers?.['x-total-count']);
  const limitHeader = Number(headers?.['x-limit']);
  const offsetHeader = Number(headers?.['x-offset']);
  const hasMoreHeader = headers?.['x-has-more'];
  const limit = Number.isFinite(limitHeader) && limitHeader > 0 ? limitHeader : fallbackLimit;
  const offset = Number.isFinite(offsetHeader) && offsetHeader >= 0 ? offsetHeader : fallbackOffset;
  const total = Number.isFinite(totalHeader) && totalHeader >= 0 ? totalHeader : null;
  const hasMore =
    hasMoreHeader !== undefined
      ? String(hasMoreHeader).trim() === '1' || String(hasMoreHeader).toLowerCase() === 'true'
      : total !== null
        ? offset + fallbackItems.length < total
        : fallbackItems.length >= limit;
  return { total, limit, offset, hasMore };
};

export async function listCompanyContactsPage(options = {}) {
  const limit = resolvePaginationLimit(options?.limit, DEFAULT_CONTACTS_PAGE_LIMIT);
  const offset = resolvePaginationOffset(options?.offset, 0);
  const params = { limit, offset };
  const trimmedSearch = (options?.search ?? '').toString().trim();
  if (trimmedSearch) params.search = trimmedSearch;
  if (options?.phone) params.phone = options.phone;
  if (options?.whatsappNumber) params.whatsapp_number = options.whatsappNumber;
  if (options?.contactId !== null && options?.contactId !== undefined && options?.contactId !== '') {
    params.contact_id = options.contactId;
  }
  const config = { params };
  if (options?.signal) config.signal = options.signal;
  const response = await api.get(v1CompanyId('/contacts', options?.companyId), config);
  const items = Array.isArray(response.data) ? response.data : [];
  const meta = parsePaginationHeaders(response.headers, items, limit, offset);
  items.forEach((contact) => upsertContactIntoLocalCache(contact));
  return {
    items,
    total: meta.total,
    limit: meta.limit,
    offset: meta.offset,
    hasMore: meta.hasMore,
  };
}

const getContactCacheIdentityKey = (contact) => {
  if (!contact || typeof contact !== 'object') return '';
  const id = contact.id ?? contact.contact_id ?? contact.contactId;
  if (id !== null && id !== undefined && String(id).trim()) return `id:${id}`;
  const identity = normalizeContactIdentity(
    contact.whatsapp_jid ||
      contact.whatsapp_lid ||
      contact.whatsapp_number ||
      contact.contact_phone ||
      contact.phone_digits ||
      contact.phone_number ||
      contact.phone ||
      ''
  );
  if (identity) return `identity:${identity}`;
  const digits = normalizeDigits(
    contact.phone_digits ||
      contact.whatsapp_number ||
      contact.contact_phone ||
      contact.phone_number ||
      contact.phone ||
      ''
  );
  return digits ? `digits:${digits}` : '';
};

export function upsertContactIntoLocalCache(contact) {
  if (!contact || typeof contact !== 'object') return [];
  try {
    const current = readContactsCacheList();
    const nextKey = getContactCacheIdentityKey(contact);
    const next = [
      contact,
      ...(Array.isArray(current)
        ? current.filter((entry) => {
            const entryKey = getContactCacheIdentityKey(entry);
            return nextKey ? entryKey !== nextKey : entry !== contact;
          })
        : []),
    ];
    writeContactsCacheList(next);
    return next;
  } catch {
    return [contact];
  }
}

export async function searchCompanyContacts(query, options = {}) {
  const trimmed = (query ?? '').toString().trim();
  if (!trimmed && !options?.contactId && !options?.phone && !options?.whatsappNumber) {
    return [];
  }
  const params = {
    limit: resolvePaginationLimit(options?.limit, 25),
    offset: resolvePaginationOffset(options?.offset, 0),
  };
  if (trimmed) params.search = trimmed;
  if (options?.phone) params.phone = options.phone;
  if (options?.whatsappNumber) params.whatsapp_number = options.whatsappNumber;
  if (options?.contactId !== null && options?.contactId !== undefined && options?.contactId !== '') {
    params.contact_id = options.contactId;
  }
  const config = { params };
  if (options?.signal) config.signal = options.signal;
  const { data } = await api.get(v1CompanyId('/contacts', options?.companyId), config);
  const normalized = Array.isArray(data) ? data : [];
  normalized.forEach((contact) => upsertContactIntoLocalCache(contact));
  return normalized;
}

// Compatibilidade legada: o nome sugere um escopo por cliente, mas historicamente
// esta chamada sempre carregou todos os contatos da empresa atual.
// Nao use esta funcao para fetch por clientId; hoje ela ignora clientId de proposito.
export async function listClientContacts(clientId, options = {}) {
  void clientId;
  return listAllCompanyContacts(options);
}

// (Opcional) Buscar 1 contato específico
export async function getClientContact(clientId, contactId) {
  const { data } = await api.get(v1CompanyId(`/contacts/${contactId}`));
  return data;
}

// Criar contato (rota: /client_contacts/:clientId)
export async function createClientContact(clientId, contact) {
  const payload = buildContactFormData(contact);
  const { data } = await api.post(v1CompanyId(`/contacts`), payload);
  upsertContactIntoLocalCache(data);
  return data;
}

// Atualizar contato (rota: /client_contacts/:clientId/:contactId)
export async function updateClientContact(clientId, contactId, contact) {
  const payload = buildContactFormData(contact);
  const { data } = await api.put(v1CompanyId(`/contacts/${contactId}`), payload);
  return data;
}

// Deletar contato (rota: /client_contacts/:clientId/:contactId)
export async function deleteClientContact(clientId, contactId) {
  await api.delete(v1CompanyId(`/contacts/${contactId}`));
  return true;
}

// --- CHAT / WHATSAPP ---
export async function sendMessageToClient(whatsappNumber, message, options = {}) {
  try {
    const rawWhatsappNumber = (whatsappNumber ?? '').toString().trim();
    const preserveRawNumber = options?.preserveRawNumber === true;
    let normalizedWhatsappNumber = '';

    if (preserveRawNumber) {
      if (rawWhatsappNumber.startsWith('+')) {
        const digits = normalizeDigits(rawWhatsappNumber);
        normalizedWhatsappNumber = digits ? `+${digits}` : '';
      } else if (rawWhatsappNumber.startsWith('00')) {
        const digits = normalizeDigits(rawWhatsappNumber).replace(/^00+/, '');
        normalizedWhatsappNumber = digits ? `+${digits}` : '';
      } else {
        normalizedWhatsappNumber = rawWhatsappNumber.replace(/\s+/g, '');
      }
    } else {
      normalizedWhatsappNumber = normalizeWhatsappJid(rawWhatsappNumber);
    }

    const hasContactId =
      options?.contactId !== null && options?.contactId !== undefined && options?.contactId !== '';
    if (!normalizedWhatsappNumber && !hasContactId) {
      throw new Error('Número do WhatsApp inválido.');
    }

    const params = new URLSearchParams();
    if (normalizedWhatsappNumber) {
      params.append('whatsapp_number', normalizedWhatsappNumber);
    }
    params.append('message', message);
    if (hasContactId) {
      params.append('contact_id', String(options.contactId));
    }
    if (options?.skipAttendantPrefix) {
      params.append('skip_attendant_prefix', 'true');
    }
    if (options?.startAttendance) {
      params.append('start_attendance', 'true');
    }
    const clientRequestId = options?.clientRequestId || options?.client_request_id;
    if (clientRequestId) {
      params.append('client_request_id', String(clientRequestId));
    }
    if (options?.quotedMessageId) {
      params.append('quoted_message_id', String(options.quotedMessageId));
    }
    if (options?.quotedMessageRemoteId) {
      params.append('quoted_message_remote_id', String(options.quotedMessageRemoteId));
    }
    if (typeof options?.quotedMessageFromMe === 'boolean') {
      params.append('quoted_message_from_me', String(options.quotedMessageFromMe));
    }
    if (options?.quotedMessagePreview) {
      params.append('quoted_message_preview', String(options.quotedMessagePreview));
    }
    const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
    if (resolvedSessionId) {
      params.append('session_id', resolvedSessionId);
    }

    const response = await api.post(
      v1CompanyId('/whatsapp/send-message-to-client', options?.companyId),
      params,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function editWhatsAppMessage(whatsappNumber, message, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    params.append('message', message);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }
    if (options?.messageType) {
      params.append('message_type', String(options.messageType));
    }
    if (options?.skipAttendantPrefix) {
      params.append('skip_attendant_prefix', 'true');
    }

    const response = await api.post('/whatsapp/edit-message', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao editar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function deleteWhatsAppMessage(whatsappNumber, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }

    const response = await api.post('/whatsapp/delete-message', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao apagar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function markWhatsAppMessageStarred(whatsappNumber, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    if (typeof options?.starred !== 'boolean') {
      throw new Error('Parâmetro "starred" obrigatório.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }
    params.append('starred', String(options.starred));

    const response = await api.post('/whatsapp/mark-message-starred', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao favoritar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function reactWhatsAppMessage(whatsappNumber, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    if (typeof options?.emoji !== 'string') {
      throw new Error('Parâmetro "emoji" obrigatório.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }
    params.append('emoji', options.emoji);

    const response = await api.post('/whatsapp/react-message', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao reagir mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function pinWhatsAppMessage(whatsappNumber, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    if (typeof options?.pin !== 'boolean') {
      throw new Error('Parâmetro "pin" obrigatório.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }
    if (options?.timeSeconds != null) {
      params.append('time_seconds', String(options.timeSeconds));
    }
    params.append('pin', String(options.pin));

    const response = await api.post('/whatsapp/pin-message', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao fixar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function forwardWhatsAppMessage(whatsappNumber, targetWhatsappNumber, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }
    const normalizedTargetNumber = normalizeWhatsappJid(targetWhatsappNumber);
    if (!normalizedTargetNumber) {
      throw new Error('Número do WhatsApp de destino inválido.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    params.append('target_whatsapp_number', normalizedTargetNumber);
    if (options?.messageId != null) {
      params.append('message_id', String(options.messageId));
    }
    if (options?.messageRemoteId) {
      params.append('message_remote_id', String(options.messageRemoteId));
    }
    if (options?.registerPending !== false) {
      params.append('register_pending', 'true');
    }
    if (options?.targetName) {
      params.append('target_name', String(options.targetName));
    }

    const response = await api.post(
      v1CompanyId('/whatsapp/forward-message', options?.companyId),
      params,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao encaminhar mensagem:', error.response?.data || error.message);
    throw error;
  }
}

export async function sendAudioToClient(whatsappNumber, audioBlob, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    const formData = new FormData();
    formData.append('whatsapp_number', normalizedWhatsappNumber);
    const audioFileName =
      typeof audioBlob?.name === 'string' && audioBlob.name.trim()
        ? audioBlob.name.trim()
        : 'audio.webm';
    formData.append('audio_file', audioBlob, audioFileName);
    if (options?.quotedMessageId) {
      formData.append('quoted_message_id', String(options.quotedMessageId));
    }
    if (options?.quotedMessageRemoteId) {
      formData.append('quoted_message_remote_id', String(options.quotedMessageRemoteId));
    }
    if (typeof options?.quotedMessageFromMe === 'boolean') {
      formData.append('quoted_message_from_me', String(options.quotedMessageFromMe));
    }
    if (options?.quotedMessagePreview) {
      formData.append('quoted_message_preview', String(options.quotedMessagePreview));
    }
    const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
    if (resolvedSessionId) {
      formData.append('session_id', resolvedSessionId);
    }

    const response = await api.post(
      v1CompanyId('/whatsapp/send-audio-to-client', options?.companyId),
      formData
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao enviar áudio:', error.response?.data || error.message);
    throw error;
  }
}

export async function sendMediaToClient(whatsappNumber, file, message, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }

    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result.split(',')[1]);
        } else {
          reject(new Error('Falha ao ler arquivo'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    params.append('file_base64', base64);
    params.append('file_name', file.name);
    if (typeof message === 'string' && message.trim()) {
      params.append('message', message.trim());
    }
    if (options?.quotedMessageId) {
      params.append('quoted_message_id', String(options.quotedMessageId));
    }
    if (options?.quotedMessageRemoteId) {
      params.append('quoted_message_remote_id', String(options.quotedMessageRemoteId));
    }
    if (typeof options?.quotedMessageFromMe === 'boolean') {
      params.append('quoted_message_from_me', String(options.quotedMessageFromMe));
    }
    if (options?.quotedMessagePreview) {
      params.append('quoted_message_preview', String(options.quotedMessagePreview));
    }
    const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
    if (resolvedSessionId) {
      params.append('session_id', resolvedSessionId);
    }

    const response = await api.post(
      v1CompanyId('/whatsapp/send-media-to-client', options?.companyId),
      params,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Erro ao enviar arquivo:', error.response?.data || error.message);
    throw error;
  }
}

export async function sendContactToClient(whatsappNumber, contact, options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp inválido.');
    }
    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);
    params.append('contact_name', contact?.name || '');
    params.append('contact_phone', contact?.phone || '');
    if (contact?.email) {
      params.append('contact_email', contact.email);
    }
    if (contact?.company) {
      params.append('contact_company', contact.company);
    }
    if (options?.quotedMessageId) {
      params.append('quoted_message_id', String(options.quotedMessageId));
    }
    if (options?.quotedMessageRemoteId) {
      params.append('quoted_message_remote_id', String(options.quotedMessageRemoteId));
    }
    if (typeof options?.quotedMessageFromMe === 'boolean') {
      params.append('quoted_message_from_me', String(options.quotedMessageFromMe));
    }
    if (options?.quotedMessagePreview) {
      params.append('quoted_message_preview', String(options.quotedMessagePreview));
    }
    const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
    if (resolvedSessionId) {
      params.append('session_id', resolvedSessionId);
    }

    const response = await api.post('/whatsapp/send-contact-to-client', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao enviar contato:', error.response?.data || error.message);
    throw error;
  }
}

export async function markMessagesAsRead(whatsappNumber, messageIds = [], options = {}) {
  try {
    const normalizedWhatsappNumber = normalizeWhatsappJid(whatsappNumber);
    if (!normalizedWhatsappNumber) {
      throw new Error('Número do WhatsApp é obrigatório para confirmar leitura.');
    }

    const params = new URLSearchParams();
    params.append('whatsapp_number', normalizedWhatsappNumber);

    const ensureArray = Array.isArray(messageIds) ? messageIds : [messageIds];
    const seenRemoteIds = new Set();
    const remoteMessageIds = [];
    ensureArray
      .map((id) => (typeof id === 'number' ? String(id) : id))
      .filter((id) => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim())
      .filter((id) => !/^\d+$/.test(id))
      .forEach((id) => {
        if (seenRemoteIds.has(id)) return;
        seenRemoteIds.add(id);
        remoteMessageIds.push(id);
      });

    if (remoteMessageIds.length === 0) {
      return { skipped: true, reason: 'no_remote_message_ids' };
    }

    remoteMessageIds.forEach((id) => {
      params.append('message_ids', id);
    });

    const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
    if (resolvedSessionId) {
      params.append('session_id', resolvedSessionId);
    }

    const response = await api.post('/whatsapp/mark-messages-as-read', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  } catch (error) {
    console.error(
      'Erro ao confirmar leitura das mensagens:',
      error.response?.data || error.message || error,
    );
    throw error;
  }
}

// -------- WhatsApp (QR/Status) via FastAPI --------

// Util pra extrair o src do <img> do HTML
function extractDataUrlFromHtml(html) {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : null;
}

function extractQrCodeDataUrl(payload) {
  if (!payload) return null;

  const normalizeBase64 = (value) => value.replace(/\s+/g, '');
  const isLikelyBase64 = (value) => {
    if (value.length < 100) return false;
    if (value.includes('<') || value.includes('>')) return false;
    return /^[A-Za-z0-9+/=\r\n]+$/.test(value);
  };
  const isLikelyUrl = (value) => /^https?:\/\//i.test(value) || value.startsWith('/');

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('data:')) {
      return trimmed;
    }

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return extractQrCodeDataUrl(JSON.parse(trimmed));
      } catch {
        return null;
      }
    }

    if (trimmed.includes('<img')) {
      return extractDataUrlFromHtml(trimmed);
    }

    if (isLikelyBase64(trimmed)) {
      return `data:image/png;base64,${normalizeBase64(trimmed)}`;
    }

    if (isLikelyUrl(trimmed)) {
      return trimmed;
    }

    return null;
  }

  if (typeof payload === 'object') {
    const candidate =
      payload.qrcode ||
      payload.qr_code ||
      payload.qrCode ||
      payload.data_url ||
      payload.dataUrl ||
      payload.qr;

    if (typeof candidate === 'string' && candidate.trim()) {
      const trimmed = candidate.trim();
      if (trimmed.startsWith('data:')) return trimmed;
      if (isLikelyBase64(trimmed)) {
        return `data:image/png;base64,${normalizeBase64(trimmed)}`;
      }
      if (isLikelyUrl(trimmed)) return trimmed;
      return trimmed;
    }
  }

  return null;
}

// Solicita a captura de QR (abre "janela" no Baileys). Se quiser bloquear até aparecer, passe { wait: true }.
export async function requestQrCode({
  ttlMs = 120000,
  wait = false,
  timeoutS = 20,
  sessionId,
  connectionId,
} = {}) {
  const normalizedConnectionId =
    connectionId === null || connectionId === undefined ? '' : String(connectionId).trim();
  const url = normalizedConnectionId
    ? v1CompanyId(`/whatsapp/connections/${encodeURIComponent(normalizedConnectionId)}/qrcode/request`)
    : v1CompanyId('/whatsapp/qrcode/request');
  const resolvedSessionId = resolveWhatsappSessionId(sessionId);
  const payload = {
    ttl_ms: ttlMs,
    wait,
    timeout_s: timeoutS,
    ...(!normalizedConnectionId && resolvedSessionId ? { session_id: resolvedSessionId } : {}),
  };
  const { data } = await api.post(url, payload, { timeout: 15000 });
  return data;
}

// Cancela/fecha a janela de QR no Baileys (idempotente)
export async function cancelQrCode({ sessionId } = {}) {
  try {
    const resolvedSessionId = resolveWhatsappSessionId(sessionId);
    const payload = resolvedSessionId ? { session_id: resolvedSessionId } : {};
    const { data } = await api.post(v1CompanyId('/whatsapp/qrcode/cancel'), payload, {
      timeout: 10000,
    });
    return data;
  } catch {
    // silencioso pra não travar UI
    return { status: 'error' };
  }
}

// Lê o status atual do WhatsApp
export async function getWhatsappStatus({ sessionId } = {}) {
  const resolvedSessionId = resolveWhatsappSessionId(sessionId);
  const response = await api.get(v1CompanyId('/whatsapp/status'), {
    params: resolvedSessionId ? { session_id: resolvedSessionId } : undefined,
  });
  return response.data.status;
}

export async function listWhatsappSessions({ refreshStatus = true } = {}) {
  const { data } = await api.get(v1CompanyId('/whatsapp/connections'), {
    params: { refresh_status: Boolean(refreshStatus) },
  });
  return normalizeWhatsappConnectionsResponse(data);
}

export async function listCompanyWhatsappConnections({ companyId, refreshStatus = false } = {}) {
  const { data } = await api.get(v1CompanyId('/whatsapp/connections', companyId), {
    params: { refresh_status: Boolean(refreshStatus) },
  });
  return normalizeWhatsappConnectionsResponse(data);
}

export async function listOutboundWhatsappSessions({ companyId = null, refreshStatus = false } = {}) {
  const { data } = await api.get(v1CompanyId('/whatsapp/outbound-sessions', companyId), {
    params: { refresh_status: Boolean(refreshStatus) },
  });
  return normalizeWhatsappConnectionsResponse(data);
}

export async function listWhatsappSessionCatalog({ companyId = null, refreshStatus = true } = {}) {
  const { data } = await api.get(v1CompanyId('/whatsapp/session-catalog', companyId), {
    params: { refresh_status: Boolean(refreshStatus) },
  });
  return normalizeWhatsappConnectionsResponse(data);
}

export async function createWhatsappSession({
  session_id = undefined,
  name = null,
  display_name = null,
  attendant_id = null,
} = {}) {
  const payload = {};
  const normalizedSessionId =
    session_id === null || session_id === undefined ? '' : String(session_id).trim();
  if (normalizedSessionId) {
    payload.session_id = normalizedSessionId;
  }
  const resolvedName = typeof name === 'string' && name.trim()
    ? name.trim()
    : typeof display_name === 'string' && display_name.trim()
      ? display_name.trim()
      : null;
  payload.name = resolvedName;
  payload.attendant_id =
    attendant_id === null || attendant_id === undefined || attendant_id === ''
      ? null
      : Number(attendant_id);
  const { data } = await api.post(v1CompanyId('/whatsapp/connections'), payload);
  return data;
}

export async function updateWhatsappSession(
  connectionId,
  {
    name = undefined,
    display_name = undefined,
    attendant_id = undefined,
  } = {}
) {
  const payload = {};
  const resolvedName = name !== undefined ? name : display_name;
  if (resolvedName !== undefined) {
    payload.name =
      typeof resolvedName === 'string' && resolvedName.trim() ? resolvedName.trim() : null;
  }
  if (attendant_id !== undefined) {
    payload.attendant_id =
      attendant_id === null || attendant_id === '' ? null : Number(attendant_id);
  }
  const { data } = await api.patch(
    v1CompanyId(`/whatsapp/connections/${encodeURIComponent(String(connectionId || '').trim())}`),
    payload
  );
  return data;
}

export async function deleteWhatsappSession(connectionId) {
  await api.delete(
    v1CompanyId(`/whatsapp/connections/${encodeURIComponent(String(connectionId || '').trim())}`)
  );
}

export async function checkWhatsappNumber(whatsappNumber) {
  const payload = { whatsapp_number: whatsappNumber };
  const { data } = await api.post(v1CompanyId('/whatsapp/check-whatsapp-number'), payload, {
    timeout: 10000,
  });
  return data;
}

export async function checkWhatsappActiveAttendance(whatsappNumber, options = {}) {
  const payload = { whatsapp_number: whatsappNumber };
  const resolvedSessionId = resolveWhatsappSessionId(options?.sessionId);
  if (resolvedSessionId) {
    payload.session_id = resolvedSessionId;
  }
  const config = {
    timeout: 10000,
  };
  if (options?.signal) {
    config.signal = options.signal;
  }
  const { data } = await api.post(v1CompanyId('/whatsapp/check-active-attendance'), payload, config);
  return data;
}

// Busca o QR atual (FastAPI retorna HTML); retorna `data:` da <img> ou `null` se 204/404 / indisponível
export async function getQrCode({ sessionId } = {}) {
  try {
    const resolvedSessionId = resolveWhatsappSessionId(sessionId);
    const response = await api.get(v1CompanyId('/whatsapp/qrcode'), {
      params: resolvedSessionId ? { session_id: resolvedSessionId } : undefined,
      headers: { Accept: 'text/html, application/json' },
      responseType: 'text',
      transformResponse: [(v) => v],
      validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
      timeout: 10000,
    });

    if (response.status === 404 || response.status === 204) {
      return null; // ainda não disponível
    }

    const dataUrl = extractQrCodeDataUrl(response.data);
    return dataUrl || null;
  } catch (error) {
    console.error('Erro ao obter QR Code:', error.response?.data || error.message);
    // mantenha o comportamento amigável: quando falhar, trate como ausente
    return null;
  }
}

export async function disconnectWhatsapp({ sessionId } = {}) {
  try {
    const resolvedSessionId = resolveWhatsappSessionId(sessionId);
    const payload = resolvedSessionId ? { session_id: resolvedSessionId } : {};
    const response = await api.post(v1CompanyId('/whatsapp/disconnect'), payload);
    return response.data;
  } catch (error) {
    console.error('Erro ao desconectar WhatsApp:', error.response?.data || error.message);
    throw error;
  }
}

export async function deleteWhatsappToken({ sessionId } = {}) {
  try {
    const resolvedSessionId = resolveWhatsappSessionId(sessionId);
    const payload = resolvedSessionId ? { session_id: resolvedSessionId } : {};
    const response = await api.post(v1CompanyId('/whatsapp/token/delete'), payload);
    return response.data;
  } catch (error) {
    console.error('Erro ao excluir token do WhatsApp:', error.response?.data || error.message);
    throw error;
  }
}

// --- WHATSAPP GROUPS ---
export async function listWhatsappGroups({
  includeParticipants = false,
  includeInactive = false,
  includeAll = false,
  sessionId = null,
} = {}) {
  const params = { include_inactive: !!includeInactive };
  // backend default é false; precisa enviar explicitamente quando queremos participantes
  params.include_participants = !!includeParticipants;
  // permite (quando suportado) ignorar filtros de permissão do atendente
  if (includeAll) params.include_all = true;
  if (sessionId) params.session_id = sessionId;
  const { data } = await api.get(v1CompanyId('/whatsapp/groups'), { params });
  return Array.isArray(data) ? data : [];
}

export async function syncWhatsappGroups({ includeParticipants = false, sessionId = null, source = null } = {}) {
  const params = { include_inactive: false };
  // backend default é false; precisa enviar explicitamente quando queremos participantes
  params.include_participants = !!includeParticipants;
  if (sessionId) params.session_id = sessionId;
  if (source) params.sync_source = source;
  const { data } = await api.post(v1CompanyId('/whatsapp/groups/sync'), null, { params });
  if (Array.isArray(data)) {
    return {
      groups: data,
      sessions: [],
      source: 'remote',
      remote_updated: true,
      fallback_used: false,
      partial: false,
      reason: null,
      message: null,
      retry_after_seconds: 0,
    };
  }
  return {
    groups: Array.isArray(data?.groups) ? data.groups : [],
    sessions: Array.isArray(data?.sessions) ? data.sessions : [],
    source: typeof data?.source === 'string' ? data.source : 'remote',
    remote_updated: data?.remote_updated !== false,
    fallback_used: data?.fallback_used === true,
    partial: data?.partial === true,
    reason: typeof data?.reason === 'string' ? data.reason : null,
    message: typeof data?.message === 'string' ? data.message : null,
    retry_after_seconds: Number.isFinite(Number(data?.retry_after_seconds))
      ? Number(data.retry_after_seconds)
      : 0,
  };
}

export async function listWhatsappGroupMessages(groupId, { limit = 50, before, since } = {}) {
  const params = {};
  if (limit) params.limit = limit;
  if (before) params.before = before;
  if (since) params.since = since;
  const { data } = await api.get(v1CompanyId(`/whatsapp/groups/${groupId}/messages`), { params });
  return Array.isArray(data) ? data : [];
}

export async function listWhatsappGroupMessageReceipts(groupId, messageId) {
  if (!groupId || !messageId) return [];
  const { data } = await api.get(
    v1CompanyId(`/whatsapp/groups/${groupId}/messages/${messageId}/receipts`)
  );
  return Array.isArray(data) ? data : [];
}

export async function listWhatsappGroupParticipants(
  groupId,
  { limit = 500, offset = 0 } = {}
) {
  const params = {};
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;
  const { data } = await api.get(v1CompanyId(`/whatsapp/groups/${groupId}/participants`), { params });
  return Array.isArray(data) ? data : [];
}

export async function syncWhatsappGroupParticipants(
  groupId,
  { fetchPhotos = false, source = null } = {}
) {
  const params = {};
  if (fetchPhotos) params.fetch_photos = true;
  if (source) params.sync_source = source;
  const { data } = await api.post(
    v1CompanyId(`/whatsapp/groups/${groupId}/participants/sync`),
    null,
    { params }
  );
  return data ?? null;
}

export async function getWhatsappGroupMetadata(groupId) {
  const { data } = await api.get(v1CompanyId(`/whatsapp/groups/${groupId}/metadata`));
  return data ?? null;
}

export async function sendWhatsappGroupMessage(groupId, message, options = {}) {
  const payload = { message };
  if (options?.quotedMessageId) payload.quotedMessageId = options.quotedMessageId;
  if (options?.quotedMessageDbId) payload.quotedMessageDbId = options.quotedMessageDbId;
  if (options?.quotedParticipant) payload.quotedParticipant = options.quotedParticipant;
  if (options?.quotedRemoteJid) payload.quotedRemoteJid = options.quotedRemoteJid;
  if (options?.quotedFromMe !== undefined) payload.quotedFromMe = options.quotedFromMe;
  if (options?.quotedPreview) payload.quotedPreview = options.quotedPreview;
  if (typeof options?.wireMessage === 'string') payload.wireMessage = options.wireMessage;
  if (Array.isArray(options?.mentions) && options.mentions.length) {
    payload.mentions = options.mentions;
  }
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/send-message`), payload);
  return data;
}

export async function sendWhatsappGroupMedia(groupId, { fileBase64, fileName, caption, mediaType }, options = {}) {
  const payload = { fileBase64, fileName, caption };
  if (mediaType) payload.mediaType = mediaType;
  if (options?.quotedMessageId) payload.quotedMessageId = options.quotedMessageId;
  if (options?.quotedMessageDbId) payload.quotedMessageDbId = options.quotedMessageDbId;
  if (options?.quotedParticipant) payload.quotedParticipant = options.quotedParticipant;
  if (options?.quotedRemoteJid) payload.quotedRemoteJid = options.quotedRemoteJid;
  if (options?.quotedFromMe !== undefined) payload.quotedFromMe = options.quotedFromMe;
  if (options?.quotedPreview) payload.quotedPreview = options.quotedPreview;
  if (typeof options?.wireCaption === 'string') payload.wireCaption = options.wireCaption;
  if (Array.isArray(options?.mentions) && options.mentions.length) {
    payload.mentions = options.mentions;
  }
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/send-media`), payload);
  return data;
}

export async function reactWhatsappGroupMessage(groupId, options = {}) {
  if (!groupId) throw new Error('Grupo inválido para reação.');
  if (typeof options?.emoji !== 'string') {
    throw new Error('Parâmetro "emoji" obrigatório.');
  }
  const payload = { emoji: options.emoji };
  if (options?.messageId != null) payload.messageId = options.messageId;
  if (options?.messageRemoteId) payload.messageRemoteId = options.messageRemoteId;
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/react-message`), payload);
  return data;
}

export async function editWhatsappGroupMessage(groupId, options = {}) {
  if (!groupId) throw new Error('Grupo inválido para edição.');
  const rawMessage = typeof options?.message === 'string' ? options.message : '';
  if (!rawMessage.trim()) {
    throw new Error('Parâmetro "message" obrigatório.');
  }
  const payload = { message: rawMessage };
  if (options?.messageId != null) payload.messageId = options.messageId;
  if (options?.messageRemoteId) payload.messageRemoteId = options.messageRemoteId;
  if (options?.messageType) payload.messageType = options.messageType;
  if (typeof options?.wireMessage === 'string') payload.wireMessage = options.wireMessage;
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/edit-message`), payload);
  return data;
}

export async function deleteWhatsappGroupMessage(groupId, options = {}) {
  if (!groupId) throw new Error('Grupo inválido para exclusão.');
  const payload = {};
  if (options?.messageId != null) payload.messageId = options.messageId;
  if (options?.messageRemoteId) payload.messageRemoteId = options.messageRemoteId;
  if (!payload.messageId && !payload.messageRemoteId) {
    throw new Error('Informe "messageId" ou "messageRemoteId".');
  }
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/delete-message`), payload);
  return data;
}

export async function updateWhatsappGroupSubject(groupId, subject) {
  const payload = { subject };
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/update-subject`), payload);
  return data;
}

export async function updateWhatsappGroupDescription(groupId, description) {
  const payload = { description: description ?? null };
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/update-description`), payload);
  return data;
}

export async function updateWhatsappGroupSetting(groupId, setting) {
  const payload = { setting };
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/update-setting`), payload);
  return data;
}

export async function updateWhatsappGroupMemberAddMode(groupId, memberAddMode) {
  const payload = { memberAddMode: Boolean(memberAddMode) };
  const { data } = await api.post(
    v1CompanyId(`/whatsapp/groups/${groupId}/update-member-add-mode`),
    payload
  );
  return data;
}

export async function updateWhatsappGroupJoinApprovalMode(groupId, joinApprovalMode) {
  const payload = { joinApprovalMode: Boolean(joinApprovalMode) };
  const { data } = await api.post(
    v1CompanyId(`/whatsapp/groups/${groupId}/update-join-approval-mode`),
    payload
  );
  return data;
}

export async function updateWhatsappGroupEphemeral(groupId, seconds) {
  const payload = { seconds: Number(seconds) };
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/update-ephemeral`), payload);
  return data;
}

export async function listWhatsappGroupJoinRequests(groupId) {
  const { data } = await api.get(v1CompanyId(`/whatsapp/groups/${groupId}/join-requests`));
  return data ?? null;
}

export async function updateWhatsappGroupJoinRequests(groupId, participants, action) {
  const payload = { participants, action };
  const { data } = await api.post(v1CompanyId(`/whatsapp/groups/${groupId}/join-requests`), payload);
  return data;
}

export async function updateWhatsappGroupParticipants(groupId, participants, action) {
  const payload = { participants, action };
  const { data } = await api.post(
    v1CompanyId(`/whatsapp/groups/${groupId}/participants-update`),
    payload
  );
  return data;
}

// --- Conversas (listas específicas) ---
export async function getConversationsInWaiting(companyId = null, options = {}) {
  const signal = options?.signal;
  const scope = options?.scope;
  const resolvedLimit = resolvePaginationLimit(options?.limit, DEFAULT_CONVERSATIONS_PAGE_LIMIT);
  const resolvedOffset = resolvePaginationOffset(options?.offset, 0);
  const lite = options?.lite ?? true;
  try {
    const resolvedCompanyId = normalizeOptionalCompanyId(companyId ?? getCompanyId());
    const rawScope = typeof scope === 'string' ? scope.trim().toLowerCase() : '';
    const normalizedScope = rawScope === 'mine' ? 'attendant' : rawScope;
    const useAllTeamRoute = normalizedScope === 'all';
    const endpoint = useAllTeamRoute
      ? '/conversations/in_waiting_all_team_conversations'
      : '/conversations/in_waiting';
    debugConversationsApi('getConversationsInWaiting', {
      companyId: resolvedCompanyId,
      endpoint: v1CompanyId(endpoint, resolvedCompanyId),
      scope: normalizedScope || 'default',
      lite,
    });
    const params = { lite: lite ? 'true' : 'false' };
    if (options?.sort_mode === 'lastMessage' || options?.sort_mode === 'arrival') {
      params.sort_mode = options.sort_mode;
    }
    if (options?.operational_view) params.operational_view = 'true';
    if (options?.start_date) params.start_date = options.start_date;
    if (options?.end_date) params.end_date = options.end_date;
    if (options?.attendant_id !== null && options?.attendant_id !== undefined && options?.attendant_id !== '') {
      params.attendant_id = options.attendant_id;
    }
    if (options?.contact_id !== null && options?.contact_id !== undefined && options?.contact_id !== '') {
      params.contact_id = options.contact_id;
    }
    if (options?.contact_whatsapp) {
      const normalizedContactWhatsapp = normalizeConversationContactFilter(options.contact_whatsapp);
      if (normalizedContactWhatsapp) {
        params.contact_whatsapp = normalizedContactWhatsapp;
      }
    }
    if (options?.transfer_request_status) {
      params.transfer_request_status = options.transfer_request_status;
    }
    if (options?.exclude_operational_workflow) {
      params.exclude_operational_workflow = 'true';
    }
    if (options?.evaluation !== null && options?.evaluation !== undefined && options?.evaluation !== '') {
      params.evaluation = options.evaluation;
    }
    if (options?.search) {
      params.search = options.search;
    }
    if (options?.message_search) {
      params.message_search = options.message_search;
    }
    if (options?.paginated) {
      params.paginated = 'true';
      if (options?.page !== null && options?.page !== undefined && options?.page !== '') {
        params.page = options.page;
      }
    }
    const data = await fetchPaginatedCollection(endpoint, {
      companyId: resolvedCompanyId,
      params,
      limit: resolvedLimit,
      offset: resolvedOffset,
      signal,
      fetchAll: options?.fetchAll,
      returnPage: options?.paginated,
      timeout: options?.timeout,
    });
    return options?.paginated ? data : (Array.isArray(data) ? data : []);
  } catch (error) {
    if (isCanceledRequestError(error)) {
      throw error;
    }
    console.error('Erro ao buscar atendimentos em espera:', error.response?.data || error.message || error);
    throw error;
  }
}

// --- Conversas Ativas ---
export async function getActiveConversations({
  operational_view,
  start_date,
  end_date,
  team_id,
  attendant_id,
  contact_id,
  contact_whatsapp,
  transfer_request_status,
  handling_status,
  exclude_operational_workflow,
  search,
  message_search,
  paginated,
  page,
  company_id,
  scope,
  lite = true,
  limit,
  offset,
  sort_mode,
} = {}, options = {}) {
  const signal = options?.signal;
  try {
    const resolvedCompanyId = normalizeOptionalCompanyId(
      company_id ?? options?.companyId ?? getCompanyId()
    );
    const params = {};
    if (operational_view) params.operational_view = 'true';
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    if (team_id) params.team_id = team_id;
    if (attendant_id) params.attendant_id = attendant_id;
    if (contact_id !== null && contact_id !== undefined && contact_id !== '') {
      params.contact_id = contact_id;
    }
    if (contact_whatsapp) {
      const normalizedContactWhatsapp = normalizeConversationContactFilter(contact_whatsapp);
      if (normalizedContactWhatsapp) {
        params.contact_whatsapp = normalizedContactWhatsapp;
      }
    }
    if (transfer_request_status) params.transfer_request_status = transfer_request_status;
    if (handling_status) params.handling_status = handling_status;
    if (exclude_operational_workflow) params.exclude_operational_workflow = 'true';
    if (search) params.search = search;
    if (message_search) params.message_search = message_search;
    if (sort_mode === 'lastMessage' || sort_mode === 'arrival') params.sort_mode = sort_mode;
    if (paginated) {
      params.paginated = 'true';
      if (page !== null && page !== undefined && page !== '') {
        params.page = page;
      }
    }
    params.lite = lite ? 'true' : 'false';
    const resolvedLimit = resolvePaginationLimit(limit, DEFAULT_CONVERSATIONS_PAGE_LIMIT);
    const resolvedOffset = resolvePaginationOffset(offset, 0);

    const rawScope = typeof scope === 'string' ? scope.trim().toLowerCase() : '';
    const normalizedScope = rawScope === 'mine' ? 'attendant' : rawScope;
    const useAllTeamRoute = normalizedScope === 'all';
    const endpoint = useAllTeamRoute
      ? '/conversations/active_all_team_conversations'
      : '/conversations/active';
    debugConversationsApi('getActiveConversations', {
      companyId: resolvedCompanyId,
      endpoint: v1CompanyId(endpoint, resolvedCompanyId),
      scope: normalizedScope || 'default',
      params,
    });
    const data = await fetchPaginatedCollection(endpoint, {
      companyId: resolvedCompanyId,
      params,
      limit: resolvedLimit,
      offset: resolvedOffset,
      signal,
      fetchAll: options?.fetchAll,
      returnPage: paginated,
      timeout: options?.timeout,
    });

    const rawConversations = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
    const filteredConversations = rawConversations.filter(
      (conversation) => !isConversationAwaitingEvaluation(conversation)
    );

    if (paginated) {
      return {
        ...(data && typeof data === 'object' ? data : {}),
        items: filteredConversations,
      };
    }
    return filteredConversations;
  } catch (error) {
    if (isCanceledRequestError(error)) {
      throw error;
    }
    console.error('Erro ao buscar conversas ativas:', error);
    throw error;
  }
}

export async function assignConversationToAttendant(
  conversationId,
  targetAttendantId,
  companyId,
  transferSummary = '',
  internalChatMessageId = null
) {
  try {
    const payload = {
      attendant_id: targetAttendantId,
      transfer_summary: transferSummary || undefined,
    };
    if (internalChatMessageId) {
      payload.internal_chat_message_id = internalChatMessageId;
    }
    const response = await api.post(
      v1CompanyId(`/conversations/assign/${conversationId}/to-attendant`, companyId),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao associar conversa:', error.response?.data || error.message);
    throw error;
  }
}

export async function respondConversationTransferRequest(
  conversationId,
  action,
  companyId
) {
  try {
    const response = await api.post(
      v1CompanyId(`/conversations/transfer-request/${conversationId}/respond`, companyId),
      { action }
    );
    return response.data;
  } catch (error) {
    console.error(
      'Erro ao responder solicitação de transferência:',
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function assignConversationToTeam(conversationId, teamId, companyId) {
  try {
    const response = await api.post(
      v1CompanyId(`/conversations/assign/${conversationId}/to-team`, companyId),
      { team_id: teamId }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao associar conversa à fila:', error.response?.data || error.message);
    throw error;
  }
}

export async function deleteConversation(conversationId, companyId) {
  try {
    const response = await api.delete(
      v1CompanyId(`/conversations/delete/${conversationId}`, companyId)
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar conversa:', error.response?.data || error.message);
    throw error;
  }
}

export async function getConversation(conversationId, companyIdOrOptions, optionsMaybe) {
  let companyId = companyIdOrOptions;
  let options = optionsMaybe;

  if (companyIdOrOptions && typeof companyIdOrOptions === 'object') {
    options = companyIdOrOptions;
    companyId = null;
  }

  const params = {};
  if (options && Object.prototype.hasOwnProperty.call(options, 'includeTeamHistory')) {
    params.include_team_history = Boolean(options.includeTeamHistory);
  }
  if (options && Object.prototype.hasOwnProperty.call(options, 'includeAttendantParticipation')) {
    params.include_attendant_participation = Boolean(options.includeAttendantParticipation);
  }
  if (options && Object.prototype.hasOwnProperty.call(options, 'includeMessages')) {
    params.include_messages = Boolean(options.includeMessages);
  } else if (options && Object.prototype.hasOwnProperty.call(options, 'include_messages')) {
    params.include_messages = Boolean(options.include_messages);
  }
  if (params.include_messages === false) {
    params.messages_limit = 0;
  } else {
    params.messages_limit = resolvePaginationLimit(
      options?.messagesLimit ?? options?.messages_limit,
      DEFAULT_CONVERSATION_DETAIL_MESSAGES_LIMIT
    );
  }

  try {
    const response = await api.get(
      v1CompanyId(`/conversations/${conversationId}`, companyId),
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar conversa:', error.response?.data || error.message);
    throw error;
  }
}

export async function getContactChatHistory(
  {
    conversationId,
    contactId,
    contactWhatsapp,
    includeTeamHistory,
    includeAttendantParticipation,
    limit,
    before,
    companyId = null,
  } = {},
  options = {}
) {
  const signal = options?.signal;
  try {
    const params = {};

    if (conversationId !== null && conversationId !== undefined && conversationId !== '') {
      params.conversation_id = conversationId;
    }
    if (contactId !== null && contactId !== undefined && contactId !== '') {
      params.contact_id = contactId;
    }
    if (includeTeamHistory !== null && includeTeamHistory !== undefined) {
      params.include_team_history = Boolean(includeTeamHistory);
    }
    if (includeAttendantParticipation !== undefined) {
      params.include_attendant_participation = Boolean(includeAttendantParticipation);
    }
    if (contactWhatsapp) {
      const normalizedContactWhatsapp = normalizeConversationContactFilter(contactWhatsapp);
      if (normalizedContactWhatsapp) {
        params.contact_whatsapp = normalizedContactWhatsapp;
      }
    }
    if (limit !== null && limit !== undefined && limit !== '') {
      const resolvedLimit = Number(limit);
      if (Number.isFinite(resolvedLimit) && resolvedLimit > 0) {
        params.limit = Math.trunc(resolvedLimit);
      }
    }
    if (before) {
      params.before = before;
    }

    const config = {};
    if (Object.keys(params).length) {
      config.params = params;
    }
    if (signal) {
      config.signal = signal;
    }

    const { data } = await api.get(
      v1CompanyId('/conversations/contact-history', companyId),
      Object.keys(config).length ? config : undefined
    );

    return {
      messages: Array.isArray(data?.messages) ? data.messages : [],
      lifecycle_events: Array.isArray(data?.lifecycle_events) ? data.lifecycle_events : [],
      has_more: Boolean(data?.has_more),
      next_before: data?.next_before ?? null,
    };
  } catch (error) {
    if (isCanceledRequestError(error)) {
      throw error;
    }
    console.error(
      'Erro ao buscar histórico do contato:',
      error.response?.data || error.message || error
    );
    throw error;
  }
}

export async function releaseConversation(conversationId, teamId, companyId) {
  const normalizedConversationId = Number(conversationId);
  if (!Number.isFinite(normalizedConversationId)) {
    throw new Error('ID do atendimento inválido para liberar.');
  }

  try {
    const payload = { team_id: teamId };
    const response = await api.post(
      v1CompanyId(`/conversations/release/${normalizedConversationId}`, companyId),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao liberar conversa:', error.response?.data || error.message);
    throw error;
  }
}

export async function cancelConversation(conversationId, companyId) {
  try {
    const response = await api.post(v1CompanyId(`/conversations/cancel/${conversationId}`, companyId));
    return response.data;
  } catch (error) {
    console.error('Erro ao cancelar conversa:', error.response?.data || error.message);
    throw error;
  }
}

const normalizeConversationIdsPayload = (conversationIds = []) => {
  const ids = Array.isArray(conversationIds) ? conversationIds : [];
  const normalizedIds = ids
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);
  if (normalizedIds.length === 0) {
    throw new Error('Selecione ao menos um atendimento.');
  }
  return { conversation_ids: normalizedIds };
};

export async function batchCloseConversations(companyId, conversationIds = []) {
  try {
    const payload = normalizeConversationIdsPayload(conversationIds);
    const response = await api.post(
      v1CompanyId('/conversations/batch/close', companyId),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao encerrar atendimentos em lote:', error.response?.data || error.message);
    throw error;
  }
}

export async function batchCancelConversations(companyId, conversationIds = []) {
  try {
    const payload = normalizeConversationIdsPayload(conversationIds);
    const response = await api.post(
      v1CompanyId('/conversations/batch/cancel', companyId),
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao cancelar atendimentos em lote:', error.response?.data || error.message);
    throw error;
  }
}

export async function listConversationComments(conversationId) {
  try {
    const { data } = await api.get(v1CompanyId(`/conversations/${conversationId}/comments`));
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`Erro ao buscar comentários da conversa ${conversationId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function createConversationComment(conversationId, payload) {
  try {
    const { data } = await api.post(v1CompanyId(`/conversations/${conversationId}/comments`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao criar comentário na conversa ${conversationId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function getForwardPendings(options = {}) {
  try {
    const params = {};
    if (Object.prototype.hasOwnProperty.call(options, 'includeResolved')) {
      params.include_resolved = Boolean(options.includeResolved);
    }
    const config = Object.keys(params).length ? { params } : undefined;
    const { data } = await api.get(v1CompanyId('/conversations/forward-pendings'), config);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao buscar pendências de encaminhamento:', error.response?.data || error.message);
    throw error;
  }
}

export async function resolveForwardPending(pendingId, payload = {}) {
  try {
    const normalizedId = Number(pendingId);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      throw new Error('ID da pendência inválido.');
    }
    const body = {};
    if (payload?.startedConversationId != null) {
      body.started_conversation_id = Number(payload.startedConversationId);
    }
    const { data } = await api.post(
      v1CompanyId(`/conversations/forward-pendings/${normalizedId}/resolve`),
      body
    );
    return data;
  } catch (error) {
    console.error('Erro ao resolver pendência de encaminhamento:', error.response?.data || error.message);
    throw error;
  }
}

export const getConversations = async (filters = {}, options = {}) => {
  try {
    const normalizedFilters = { ...filters };
    const signal = options?.signal;
    const resolvedCompanyId = normalizeOptionalCompanyId(options?.companyId ?? getCompanyId());
    // Aliases frontend → backend
    if (normalizedFilters.attendantId && !normalizedFilters.attendant_id) {
      normalizedFilters.attendant_id = normalizedFilters.attendantId;
    }
    if (!normalizedFilters.contact_whatsapp) {
      const rawWhats =
        normalizedFilters.whatsapp_number ||
        normalizedFilters.whatsappNumber ||
        normalizedFilters.contactWhatsapp ||
        null;
      const normalizedContactWhatsapp = rawWhats
        ? normalizeConversationContactFilter(rawWhats)
        : null;
      if (normalizedContactWhatsapp) {
        normalizedFilters.contact_whatsapp = normalizedContactWhatsapp;
      }
    }

    const params = {};
    Object.entries(normalizedFilters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params[key] = value;
    });
    const lite = options?.lite ?? true;
    params.lite = lite ? 'true' : 'false';
    if (options && Object.prototype.hasOwnProperty.call(options, 'includeTeamHistory')) {
      params.include_team_history = options.includeTeamHistory ? 'true' : 'false';
    }
    if (options?.paginated) {
      params.paginated = 'true';
      if (options?.page !== null && options?.page !== undefined && options?.page !== '') {
        params.page = options.page;
      }
    }

    const limit = resolvePaginationLimit(options?.limit, DEFAULT_CONVERSATIONS_PAGE_LIMIT);
    const offset = resolvePaginationOffset(options?.offset, 0);
    debugConversationsApi('getConversations', {
      companyId: resolvedCompanyId,
      endpoint: v1CompanyId('/conversations', resolvedCompanyId),
      params,
    });
    return await fetchPaginatedCollection('/conversations', {
      companyId: resolvedCompanyId,
      params,
      limit,
      offset,
      signal,
      fetchAll: resolveFetchAllOption(options?.fetchAll ?? false),
      maxPages: options?.maxPages,
      returnPage: options?.paginated,
    });
  } catch (error) {
    if (isCanceledRequestError(error)) {
      throw error;
    }
    console.error('Erro ao buscar conversas:', error.response?.data || error.message || error);
    throw error;
  }
};

// --- Encerrar atendimento ---
export async function serviceClosed(chatId, companyId = null) {
  try {
    const normalizedChatId = Number(chatId);
    if (!Number.isInteger(normalizedChatId) || normalizedChatId <= 0) {
      throw new Error('ID do atendimento inválido para encerrar.');
    }
    const resolvedCompanyId = normalizeOptionalCompanyId(companyId ?? getCompanyId());
    const endpoint = v1CompanyId(`/conversations/${normalizedChatId}/close`, resolvedCompanyId);
    debugConversationsApi('serviceClosed', {
      companyId: resolvedCompanyId,
      chatId: normalizedChatId,
      endpoint,
    });

    const response = await api.post(endpoint);
    return response.data;
  } catch (error) {
    console.error('Erro ao encerrar atendimento:', {
      chatId,
      companyId,
      status: error?.response?.status,
      response: error?.response?.data,
      message: error?.message,
    });
    throw error;
  }
}

// --- QUEUES (endpoint legado /teams) ---//
export async function getTeams(companyIdOverride = null, options = {}) {
  const companyScope = companyIdOverride ?? getCompanyId() ?? 'current';
  return getCachedRequestValue(
    `teams:${companyScope}`,
    REQUEST_CACHE_TTLS_MS.teams,
    async () => {
      const response = await api.get(v1CompanyId('/teams', companyIdOverride));
      return response.data;
    },
    { force: options?.force === true }
  );
}

export async function createTeam(data) {
  try {
    const companyId = getCompanyId();
    const payload = { ...data, company_id: companyId };
    const response = await api.post('/teams', payload);
    clearRequestCache();
    return response.data;
  } catch (error) {
    console.error('Erro ao criar fila:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateTeam(id, data) {
  try {
    const companyId = getCompanyId();
    const payload = { ...data, company_id: companyId };
    const response = await api.put(`/teams/${id}`, payload);
    clearRequestCache();
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar fila ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteTeam(id) {
  try {
    await api.delete(`/teams/${id}`);
    clearRequestCache();
    return true;
  } catch (error) {
    console.error(`Erro ao deletar fila ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function getTeamById(id) {
  try {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao buscar fila ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- ROLE CATALOG (usado no cadastro/edição de atendentes) ---
export async function getRoles() {
  try {
    const response = await api.get(`/${API_VERSION}/roles`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar roles:', error.response?.data || error.message);
    throw error;
  }
}

export async function getRoleById(roleId, options = {}) {
  try {
    return await getCachedRequestValue(
      `role:${roleId}`,
      REQUEST_CACHE_TTLS_MS.role,
      async () => {
        const response = await api.get(`/${API_VERSION}/roles/${roleId}`);
        return response.data;
      },
      { force: options?.force === true }
    );
  } catch (error) {
    console.error(`Erro ao buscar role ${roleId}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- SUPER ADMIN AGENTS ---
export async function listSuperAdminAgents() {
  try {
    const { data } = await api.get(v1('/super-admin/agents'));
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar usuários Agent.');
    console.error('Erro ao buscar usuários Agent:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function createSuperAdminAgent(payload) {
  try {
    const { data } = await api.post(v1('/super-admin/agents'), payload);
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao criar usuário Agent.');
    console.error('Erro ao criar usuário Agent:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

// --- SUPER ADMIN: WEBHOOKS SEND-MESSAGE ---
export async function listSendMessageWebhooks() {
  try {
    const { data } = await api.get(v1('/super-admin/webhooks/send-message'));
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao carregar webhooks de envio.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function createSendMessageWebhook(payload) {
  try {
    const { data } = await api.post(v1('/super-admin/webhooks/send-message'), payload);
    return data;
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao criar webhook de envio.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function updateSendMessageWebhook(webhookId, payload) {
  try {
    const { data } = await api.patch(
      v1(`/super-admin/webhooks/send-message/${webhookId}`),
      payload
    );
    return data;
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao atualizar webhook de envio.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function rotateSendMessageWebhookToken(webhookId) {
  try {
    const { data } = await api.post(
      v1(`/super-admin/webhooks/send-message/${webhookId}/rotate-token`)
    );
    return data;
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao gerar um novo token.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function listSendMessageWebhookDeliveries(webhookId, limit = 50) {
  try {
    const { data } = await api.get(
      v1(`/super-admin/webhooks/send-message/${webhookId}/deliveries`),
      { params: { limit } }
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao carregar os envios do webhook.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function deleteSendMessageWebhook(webhookId) {
  try {
    await api.delete(v1(`/super-admin/webhooks/send-message/${webhookId}`));
    return true;
  } catch (error) {
    const normalizedError = new Error(
      getApiErrorMessage(error, 'Erro ao excluir webhook de envio.')
    );
    normalizedError.cause = error;
    throw normalizedError;
  }
}

// --- COMPANY SETTINGS ---
export async function getCompany() {
  return getCachedRequestValue(
    'companies:list',
    REQUEST_CACHE_TTLS_MS.companies,
    async () => {
      const { data } = await api.get(v1('/companies/list'));
      return data;
    }
  );
}

export async function getCompanySummaries(companyId = null, options = {}) {
  const includeMediaVolume = options?.includeMediaVolume === true;
  const params = {};
  if (companyId !== null && companyId !== undefined) {
    params.company_id = companyId;
  }
  if (includeMediaVolume) {
    params.include_media_volume = true;
  }
  const { data } = await api.get(v1('/companies/summary'), {
    params: Object.keys(params).length ? params : undefined,
  });
  return Array.isArray(data) ? data : [];
}

export async function getCompanyById(id, options = {}) {
  return getCachedRequestValue(
    `company:${id}`,
    REQUEST_CACHE_TTLS_MS.company,
    async () => {
      const { data } = await api.get(v1(`/companies/${id}`));
      return data;
    },
    { force: options?.force === true }
  );
}

export async function downloadCompanyBackup(companyId) {
  const response = await api.get(v1(`/companies/${companyId}/backup`), {
    responseType: 'blob',
  });

  const contentDisposition =
    response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'] || '';
  let filename = `bluezap_company_${companyId}_backup.sql`;

  const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8FilenameMatch?.[1]) {
    try {
      filename = decodeURIComponent(utf8FilenameMatch[1].trim());
    } catch {
      filename = utf8FilenameMatch[1].trim();
    }
  } else {
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (filenameMatch?.[1]) {
      filename = filenameMatch[1].trim();
    }
  }

  const blob = new Blob([response.data], {
    type: response.data?.type || 'application/sql',
  });
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);

  return {
    filename,
    size: blob.size,
  };
}

export async function restoreCompanyBackup(companyId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(v1(`/companies/${companyId}/backup/restore`), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function restoreCompanyBackupAsNew(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(v1('/companies/backup/restore-new'), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function previewCompanyBackup(companyId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(v1(`/companies/${companyId}/backup/preview`), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function createCompany(data) {
  const response = await api.post(v1(`/companies`), data);
  return response.data;
}

export async function updateCompany(id, data) {
  const response = await api.put(v1(`/companies/${id}`), data);
  return response.data;
}

export async function deactivateCompanyUsers(id) {
  const response = await api.post(v1(`/companies/${id}/deactivate-users`));
  return response.data;
}

export async function cancelOpenCompanyConversations(id) {
  const response = await api.post(v1(`/companies/${id}/conversations/cancel-open`));
  return response.data;
}

export async function deleteCompany(id) {
  const response = await api.delete(v1(`/companies/${id}`));
  return response.data;
}

export async function startDeleteCompanyAsync(id) {
  const response = await api.post(v1(`/companies/${id}/delete-async`));
  return response.data;
}

export async function getCompanyDeleteJob(jobId) {
  const response = await api.get(v1(`/companies/delete-jobs/${jobId}`));
  return response.data;
}

export async function purgeCompanyContacts(companyId) {
  const response = await api.delete(v1CompanyId('/contacts/all_contacts', companyId));
  return response.data;
}

export async function getWorkDays() {
  const companyId = getCompanyId();
  const { data } = await api.get(v1(`companies/${companyId}`));
  const existing = data.work_days || [];
  const allDays = Array.from({ length: 7 }, (_, i) => i);
  return allDays.map((day) => {
    const found = existing.find((d) => Number(d.day_of_week) === day) || {};
    return {
      id: found.id ?? day,
      day_of_week: day,
      start_time: found.start_time ?? '00:00:00',
      end_time: found.end_time ?? '00:00:00',
    };
  });
}

export async function updateWorkDays(workDays) {
  const companyId = getCompanyId();
  const response = await api.put(v1(`companies/${companyId}`), {
    work_days: workDays,
  });
  return response.data;
}

// --- WORKFLOWS ---
export async function downloadDailyWorkflowBackup() {
  const { data: backup } = await api.post(v1('/workflow-backups'));
  const fileName = String(backup?.file_name || '').trim();
  if (!fileName) {
    throw new Error('A API não informou o arquivo do backup de workflows.');
  }

  const response = await api.get(v1(`/workflow-backups/${encodeURIComponent(fileName)}`), {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], {
    type: response.data?.type || 'application/zip',
  });
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);

  return {
    ...backup,
    file_name: fileName,
    size_bytes: blob.size,
  };
}

export async function listWorkflows({ companyId = null } = {}) {
  try {
    const { data } = await api.get(v1CompanyId('/workflows', companyId));
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao listar workflows:', error.response?.data || error.message);
    throw error;
  }
}

export async function getWorkflowById(workflowId) {
  try {
    const { data } = await api.get(v1CompanyId(`/workflows/${workflowId}`));
    return data;
  } catch (error) {
    console.error(`Erro ao buscar workflow ${workflowId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function createWorkflow(payload, { portableImport = false } = {}) {
  try {
    const config = portableImport ? { params: { portable_import: true } } : undefined;
    const { data } = await api.post(v1CompanyId('/workflows'), payload, config);
    return data;
  } catch (error) {
    console.error('Erro ao criar workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateWorkflowById(workflowId, payload, { portableImport = false } = {}) {
  try {
    const config = portableImport ? { params: { portable_import: true } } : undefined;
    const { data } = await api.put(v1CompanyId(`/workflows/${workflowId}`), payload, config);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar workflow ${workflowId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteWorkflowById(workflowId) {
  try {
    await api.delete(v1CompanyId(`/workflows/${workflowId}`));
    return true;
  } catch (error) {
    console.error(`Erro ao remover workflow ${workflowId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function previewWorkflowExternalApi(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/workflows/preview/external-api'), payload || {});
    return data || {};
  } catch (error) {
    console.error('Erro ao executar preview de API externa do workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function previewWorkflowBling(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/workflows/preview/bling'), payload || {});
    return data || {};
  } catch (error) {
    console.error('Erro ao executar preview do Bling no workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function previewWorkflowConfiguredPythonAction(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/workflows/preview/python-action'), payload || {});
    return data || {};
  } catch (error) {
    console.error('Erro ao executar preview Python no workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function previewWorkflowIaInteract(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/workflows/preview/ia-interact'), payload || {});
    return data || {};
  } catch (error) {
    console.error('Erro ao testar conexão IA do workflow:', error.response?.data || error.message);
    throw error;
  }
}

// --- WORKFLOW ACTIONS (SUPER ADMIN CATALOG) ---
export async function listWorkflowActions({
  includeInactive = false,
  includeAllScopes = false,
  companyId = null,
} = {}) {
  try {
    const params = {};
    if (includeInactive) params.include_inactive = true;
    if (includeAllScopes) {
      params.include_all_scopes = true;
    } else {
      const resolvedCompanyId = normalizeOptionalCompanyId(companyId ?? getCompanyId());
      if (resolvedCompanyId !== null) params.company_id = resolvedCompanyId;
    }
    const { data } = await api.get(v1('/workflow-actions'), {
      params: Object.keys(params).length ? params : undefined,
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao listar ações de workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function getWorkflowActionById(actionId) {
  try {
    const { data } = await api.get(v1(`/workflow-actions/${actionId}`));
    return data;
  } catch (error) {
    console.error(`Erro ao buscar ação de workflow ${actionId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function createWorkflowAction(payload) {
  try {
    const { data } = await api.post(v1('/workflow-actions'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar ação de workflow:', error.response?.data || error.message);
    throw error;
  }
}

export async function previewWorkflowPythonAction(payload) {
  try {
    const { data } = await api.post(v1('/workflow-actions/preview-python'), payload || {});
    return data || {};
  } catch (error) {
    console.error('Erro ao executar preview Python da ação:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateWorkflowAction(actionId, payload) {
  try {
    const { data } = await api.put(v1(`/workflow-actions/${actionId}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar ação de workflow ${actionId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deactivateWorkflowAction(actionId) {
  try {
    await api.delete(v1(`/workflow-actions/${actionId}`));
    return true;
  } catch (error) {
    console.error(`Erro ao desativar ação de workflow ${actionId}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteWorkflowAction(actionId, { force = false } = {}) {
  try {
    const params = force ? { force: true } : undefined;
    await api.delete(v1(`/workflow-actions/${actionId}/permanent`), { params });
    return true;
  } catch (error) {
    console.error(
      `Erro ao excluir definitivamente a ação de workflow ${actionId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function syncWorkflowActionCatalog({
  overwriteExisting = false,
  reactivateExisting = true,
} = {}) {
  try {
    const params = {};
    if (overwriteExisting) params.overwrite_existing = true;
    if (!reactivateExisting) params.reactivate_existing = false;
    const { data } = await api.post(
      v1('/workflow-actions/sync-catalog'),
      null,
      Object.keys(params).length ? { params } : undefined
    );
    return data || {};
  } catch (error) {
    console.error('Erro ao sincronizar catálogo de ações de workflow:', error.response?.data || error.message);
    throw error;
  }
}

// --- RESPONSE TEMPLATES ---
export async function listResponseTemplates() {
  try {
    const response = await api.get(v1CompanyId('/response-templates'));
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar respostas automáticas:', error.response?.data || error.message);
    throw error;
  }
}

const responseTemplatePayloadToRequestBody = (payload = {}, { isUpdate = false } = {}) => {
  const mediaFile = payload.mediaFile || payload.media_file || null;
  const mediaType = payload.mediaType || payload.media_type || null;
  const mediaAction = payload.mediaAction || payload.media_action || null;

  if (!mediaFile) {
    const body = {};
    if (!isUpdate && payload.scope !== undefined) body.scope = payload.scope;
    if (payload.title !== undefined) body.title = payload.title;
    if (payload.content !== undefined) body.content = payload.content;
    if (mediaAction) body.media_action = mediaAction;
    if (mediaType) body.media_type = mediaType;
    return body;
  }

  const formData = new FormData();
  if (!isUpdate && payload.scope !== undefined) formData.append('scope', payload.scope);
  if (payload.title !== undefined) formData.append('title', payload.title);
  if (payload.content !== undefined) formData.append('content', payload.content);
  if (mediaType) formData.append('media_type', mediaType);
  if (isUpdate) formData.append('media_action', mediaAction || 'replace');
  formData.append('media_file', mediaFile);
  return formData;
};

export async function createResponseTemplate(data) {
  try {
    const response = await api.post(
      v1CompanyId('/response-templates'),
      responseTemplatePayloadToRequestBody(data)
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao criar resposta automática:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateResponseTemplate(id, data) {
  try {
    const response = await api.put(
      v1CompanyId(`/response-templates/${id}`),
      responseTemplatePayloadToRequestBody(data, { isUpdate: true })
    );
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar resposta automática ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export function getResponseTemplateMediaUrl(id) {
  return `/api${v1CompanyId(`/response-templates/${id}/media`)}`;
}

export async function getResponseTemplateMedia(id) {
  try {
    const response = await api.get(v1CompanyId(`/response-templates/${id}/media`), {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error(`Erro ao baixar mídia da resposta automática ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteResponseTemplate(id) {
  try {
    await api.delete(v1CompanyId(`/response-templates/${id}`));
    return true;
  } catch (error) {
    console.error(`Erro ao remover resposta automática ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- TRANSMISSIONS / CAMPANHAS ---
export async function listTransmissions() {
  try {
    const { data } = await api.get(v1CompanyId('/transmissions'));
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao listar transmissões:', error.response?.data || error.message);
    throw error;
  }
}

export async function getTransmissionDetails(id) {
  try {
    const { data } = await api.get(v1CompanyId(`/transmissions/${id}`));
    return data;
  } catch (error) {
    console.error(`Erro ao carregar transmissão ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function createTransmission(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/transmissions'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar transmissão:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateTransmission(id, payload) {
  try {
    const { data } = await api.put(v1CompanyId(`/transmissions/${id}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar transmissão ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function startTransmission(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/${id}/start`));
    return data;
  } catch (error) {
    console.error(`Erro ao iniciar transmissão ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function pauseTransmission(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/${id}/pause`));
    return data;
  } catch (error) {
    console.error(`Erro ao pausar transmissão ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function cancelTransmission(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/${id}/cancel`));
    return data;
  } catch (error) {
    console.error(`Erro ao cancelar transmissão ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

const normalizeScheduleCounts = (items = [], counts = null) => {
  if (counts && typeof counts === 'object') {
    return {
      all: Number(counts.all ?? 0),
      upcoming: Number(counts.upcoming ?? 0),
      sent: Number(counts.sent ?? 0),
      cancelled: Number(counts.cancelled ?? 0),
    };
  }
  return items.reduce(
    (acc, schedule) => {
      if (!['scheduled', 'processing', 'sent', 'cancelled'].includes(schedule?.status)) return acc;
      acc.all += 1;
      if (['scheduled', 'processing'].includes(schedule.status)) acc.upcoming += 1;
      if (schedule.status === 'sent') acc.sent += 1;
      if (schedule.status === 'cancelled') acc.cancelled += 1;
      return acc;
    },
    { all: 0, upcoming: 0, sent: 0, cancelled: 0 },
  );
};

export async function listMessageSchedules(params = {}) {
  try {
    const { data } = await api.get(v1CompanyId('/transmissions/schedules'), { params });
    if (Array.isArray(data)) {
      return {
        items: data,
        total: data.length,
        limit: data.length,
        offset: 0,
        counts: normalizeScheduleCounts(data),
      };
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items,
      total: Number(data?.total ?? 0),
      limit: Number(data?.limit ?? params.limit ?? 50),
      offset: Number(data?.offset ?? params.offset ?? 0),
      counts: normalizeScheduleCounts(items, data?.counts),
    };
  } catch (error) {
    console.error('Erro ao listar agendamentos:', error.response?.data || error.message);
    throw error;
  }
}

export async function getMessageScheduleDetails(id) {
  try {
    const { data } = await api.get(v1CompanyId(`/transmissions/schedules/${id}`));
    return data;
  } catch (error) {
    console.error(`Erro ao carregar agendamento ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function createMessageSchedule(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/transmissions/schedules'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar agendamento:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateMessageSchedule(id, payload) {
  try {
    const { data } = await api.put(v1CompanyId(`/transmissions/schedules/${id}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar agendamento ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function cancelMessageSchedule(id, payload = undefined) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/schedules/${id}/cancel`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao cancelar agendamento ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function listMessageScheduleSeries(params = {}) {
  try {
    const { data } = await api.get(v1CompanyId('/transmissions/schedule-series'), { params });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
      limit: Number(data?.limit ?? params.limit ?? 50),
      offset: Number(data?.offset ?? params.offset ?? 0),
    };
  } catch (error) {
    console.error('Erro ao listar recorrências:', error.response?.data || error.message);
    throw error;
  }
}

export async function createMessageScheduleSeries(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/transmissions/schedule-series'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar recorrência:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateMessageScheduleSeries(id, payload) {
  try {
    const { data } = await api.put(v1CompanyId(`/transmissions/schedule-series/${id}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar recorrência ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function pauseMessageScheduleSeries(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/schedule-series/${id}/pause`));
    return data;
  } catch (error) {
    console.error(`Erro ao pausar recorrência ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function resumeMessageScheduleSeries(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/schedule-series/${id}/resume`));
    return data;
  } catch (error) {
    console.error(`Erro ao retomar recorrência ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function cancelMessageScheduleSeries(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/schedule-series/${id}/cancel`));
    return data;
  } catch (error) {
    console.error(`Erro ao cancelar recorrência ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function listMessageScheduleTemplates(params = {}) {
  try {
    const { data } = await api.get(v1CompanyId('/transmissions/schedule-templates'), { params });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
      limit: Number(data?.limit ?? params.limit ?? 50),
      offset: Number(data?.offset ?? params.offset ?? 0),
    };
  } catch (error) {
    console.error('Erro ao listar cenários salvos:', error.response?.data || error.message);
    throw error;
  }
}

export async function createMessageScheduleTemplate(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/transmissions/schedule-templates'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar cenário salvo:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateMessageScheduleTemplate(id, payload) {
  try {
    const { data } = await api.put(v1CompanyId(`/transmissions/schedule-templates/${id}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar cenário salvo ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function duplicateMessageScheduleTemplate(id) {
  try {
    const { data } = await api.post(v1CompanyId(`/transmissions/schedule-templates/${id}/duplicate`));
    return data;
  } catch (error) {
    console.error(`Erro ao duplicar cenário salvo ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteMessageScheduleTemplate(id) {
  try {
    const { data } = await api.delete(v1CompanyId(`/transmissions/schedule-templates/${id}`));
    return data;
  } catch (error) {
    console.error(`Erro ao excluir cenário salvo ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- COMPANY NOTICES ---
export async function getCompanyNotices(options = {}) {
  const { onlyActive = false } = options ?? {};
  try {
    const params = {};
    if (onlyActive) params.only_active = true;
    const { data } = await api.get(v1CompanyId('/company-notices'), {
      params,
    });
    return data;
  } catch (error) {
    console.error('Erro ao buscar avisos da empresa:', error.response?.data || error.message);
    throw error;
  }
}

export async function getNoticeBoard() {
  try {
    const { data } = await api.get(v1CompanyId('/company-notices/board'));
    return data;
  } catch (error) {
    console.error('Erro ao carregar mural de avisos:', error.response?.data || error.message);
    throw error;
  }
}

export async function createCompanyNotice(payload) {
  try {
    const { data } = await api.post(v1CompanyId('/company-notices'), payload);
    return data;
  } catch (error) {
    console.error('Erro ao criar aviso:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateCompanyNotice(id, payload) {
  try {
    const { data } = await api.put(v1CompanyId(`/company-notices/${id}`), payload);
    return data;
  } catch (error) {
    console.error(`Erro ao atualizar aviso ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

export async function deleteCompanyNotice(id) {
  try {
    await api.delete(v1CompanyId(`/company-notices/${id}`));
    return true;
  } catch (error) {
    console.error(`Erro ao remover aviso ${id}:`, error.response?.data || error.message);
    throw error;
  }
}

// --- CLIENT UPDATES (Super Admin) ---
export async function listClientUpdates(options = {}) {
  const { onlyActive = false } = options ?? {};
  try {
    const params = {};
    if (onlyActive) params.only_active = true;
    const { data } = await api.get(v1('/client-updates'), { params });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar atualizações globais.');
    console.error('Erro ao buscar atualizações globais:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getClientUpdatesBoard() {
  const companyId = getCompanyId();
  if (companyId === null || companyId === undefined || Number.isNaN(Number(companyId))) {
    throw new Error('company_id não definido; selecione uma empresa antes de continuar.');
  }

  try {
    const { data } = await api.get(v1('/client-updates/board'), {
      params: { company_id: companyId },
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao carregar mural de novidades.');
    console.error('Erro ao carregar mural de novidades:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function createClientUpdate(payload) {
  try {
    const { data } = await api.post(v1('/client-updates'), payload);
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao criar atualização.');
    console.error('Erro ao criar atualização:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function updateClientUpdate(id, payload) {
  try {
    const { data } = await api.put(v1(`/client-updates/${id}`), payload);
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao atualizar atualização.');
    console.error(`Erro ao atualizar atualização ${id}:`, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function deleteClientUpdate(id) {
  try {
    await api.delete(v1(`/client-updates/${id}`));
    return true;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao remover atualização.');
    console.error(`Erro ao remover atualização ${id}:`, error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function updateClientUpdatesPreference(preference) {
  const companyId = getCompanyId();
  if (companyId === null || companyId === undefined || Number.isNaN(Number(companyId))) {
    throw new Error('company_id não definido; selecione uma empresa antes de continuar.');
  }

  try {
    const { data } = await api.put(v1('/client-updates/preferences/me'), preference, {
      params: { company_id: companyId },
    });
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao salvar preferência.');
    console.error('Erro ao salvar preferência de novidades:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    throw normalizedError;
  }
}

export async function getClientUpdatesAckStatus() {
  const companyId = getCompanyId();
  if (companyId === null || companyId === undefined || Number.isNaN(Number(companyId))) {
    throw new Error('company_id não definido; selecione uma empresa antes de continuar.');
  }

  try {
    const { data } = await api.get(v1('/client-updates/ack'), {
      params: { company_id: companyId },
    });
    return {
      acknowledged: Boolean(data?.acknowledged ?? data?.ok ?? data === true),
      marker: typeof data?.marker === 'string' ? data.marker : '',
    };
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao verificar confirmação das novidades.');
    console.error('Erro ao verificar confirmação das novidades:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    normalizedError.status = error?.response?.status ?? error?.status;
    throw normalizedError;
  }
}

export async function acknowledgeClientUpdates() {
  const companyId = getCompanyId();
  if (companyId === null || companyId === undefined || Number.isNaN(Number(companyId))) {
    throw new Error('company_id não definido; selecione uma empresa antes de continuar.');
  }

  try {
    const { data } = await api.post(
      v1('/client-updates/ack'),
      {},
      {
        params: { company_id: companyId },
      }
    );
    return {
      acknowledged: Boolean(data?.acknowledged ?? data?.ok ?? data === true),
      marker: typeof data?.marker === 'string' ? data.marker : '',
    };
  } catch (error) {
    const message = getApiErrorMessage(error, 'Erro ao confirmar leitura das novidades.');
    console.error('Erro ao confirmar leitura das novidades:', error.response?.data || error.message);
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    normalizedError.status = error?.response?.status ?? error?.status;
    throw normalizedError;
  }
}

export async function createBluezapBiSession(companyId) {
  try {
    const { data } = await api.post(v1('/bi/session'), null, {
      params: companyId ? { company_id: companyId } : undefined,
    });
    if (!data?.url) {
      throw new Error('O Bluezap não retornou o endereço de acesso ao BI.');
    }
    return data;
  } catch (error) {
    const message = getApiErrorMessage(error, 'Não foi possível abrir o Bluezap BI.');
    const normalizedError = new Error(message);
    normalizedError.cause = error;
    normalizedError.status = error?.response?.status ?? error?.status;
    throw normalizedError;
  }
}
